import React, { useState, useEffect } from 'react';
import { Plane, Plus, Search, Calendar, MapPin, Download, Edit2, Trash2, Clock, ArrowRight, X, QrCode, Pencil, Check, Heart, Star, Filter, ChevronRight, MoreHorizontal, Hotel, Globe } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { travelAPI } from '../services/api';
import FlightTracker from '../components/FlightTracker';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { useAuth } from '../context/UserAuthContext';
import { getAirportCity } from '../utils/airportCities';

// Convert any flight time format ("14:30", "14:30:00", "2:30 PM") to a clean
// 12-hour string like "2:30 PM". Returns input unchanged if not parseable.
const formatFlightTime12h = (raw) => {
  if (!raw) return '';
  const str = String(raw).trim();
  // Already 12-hour with AM/PM — leave it alone
  if (/(AM|PM|am|pm)$/.test(str)) return str.toUpperCase().replace('AM', 'AM').replace('PM', 'PM');
  // 24-hour HH:MM (or HH:MM:SS)
  const m = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (m) {
    const hours = parseInt(m[1], 10);
    const mins = m[2];
    if (isNaN(hours) || hours < 0 || hours > 23) return str;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const h12 = hours % 12 || 12;
    return `${h12}:${mins} ${ampm}`;
  }
  return str;
};

// Rich destination data with real pricing, booking links, and filter tags
const EXPLORE_TRIPS = [
  {
    id: 1,
    name: 'Maldives',
    description: 'Overwater bungalows, crystal lagoons, and pristine coral reefs in the Indian Ocean.',
    destination: 'Malé, Maldives',
    duration: '5–8 days',
    rating: 4.9,
    price: 'From $1,500',
    priceNum: 1500,
    flightDuration: '~20h from NYC',
    bestFor: 'Honeymoon · Luxury · Snorkeling',
    bestSeason: 'Nov – Apr (dry season)',
    highlights: ['Private overwater villas', 'World-class snorkeling & diving', 'Bioluminescent beach nights'],
    tags: ['popular', 'luxury'],
    image: 'https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    bookFlightUrl: 'https://www.google.com/travel/flights?q=flights+to+Male+Maldives',
    bookHotelUrl: 'https://www.booking.com/searchresults.html?ss=Maldives',
  },
  {
    id: 2,
    name: 'Santorini',
    description: 'Iconic white-washed cliffs, volcanic beaches and the most breathtaking sunsets in Europe.',
    destination: 'Santorini, Greece',
    duration: '4–7 days',
    rating: 4.8,
    price: 'From $850',
    priceNum: 850,
    flightDuration: '~11h from NYC',
    bestFor: 'Romance · Photography · Wine',
    bestSeason: 'Apr – Oct (warm & sunny)',
    highlights: ['Oia sunset cliffs', 'Volcanic black-sand beaches', 'Cave hotel stays'],
    tags: ['popular', 'luxury'],
    image: 'https://images.unsplash.com/photo-1660315249137-29c09bd829a8?crop=entropy&cs=srgb&fm=jpg&w=600&q=80',
    bookFlightUrl: 'https://www.google.com/travel/flights?q=flights+to+Santorini+Greece',
    bookHotelUrl: 'https://www.booking.com/searchresults.html?ss=Santorini+Greece',
  },
  {
    id: 3,
    name: 'Bali',
    description: 'Lush rice terraces, ancient temples, luxury resorts and vibrant culture at unbeatable prices.',
    destination: 'Bali, Indonesia',
    duration: '6–10 days',
    rating: 4.8,
    price: 'From $750',
    priceNum: 750,
    flightDuration: '~22h from NYC',
    bestFor: 'Culture · Wellness · Budget Luxury',
    bestSeason: 'Apr – Sep (dry season)',
    highlights: ['Ubud rice terrace trek', 'Uluwatu temple at sunset', 'Seminyak beach clubs'],
    tags: ['popular', 'discount'],
    image: 'https://images.pexels.com/photos/32070172/pexels-photo-32070172.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    bookFlightUrl: 'https://www.google.com/travel/flights?q=flights+to+Bali+Indonesia',
    bookHotelUrl: 'https://www.booking.com/searchresults.html?ss=Bali+Indonesia',
  },
  {
    id: 4,
    name: 'Tokyo',
    description: 'A seamless blend of futuristic technology, ancient culture, world-class cuisine and neon-lit nightlife.',
    destination: 'Tokyo, Japan',
    duration: '7–10 days',
    rating: 4.9,
    price: 'From $700',
    priceNum: 700,
    flightDuration: '~14h from NYC',
    bestFor: 'Food · Culture · Shopping',
    bestSeason: 'Mar – May (cherry blossom) · Sep – Nov',
    highlights: ['Shibuya Crossing at night', 'Tsukiji fish market breakfast', 'Day trip to Mt. Fuji'],
    tags: ['popular'],
    image: 'https://images.pexels.com/photos/439741/pexels-photo-439741.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    bookFlightUrl: 'https://www.google.com/travel/flights?q=flights+to+Tokyo+Japan',
    bookHotelUrl: 'https://www.booking.com/searchresults.html?ss=Tokyo+Japan',
  },
  {
    id: 5,
    name: 'Cancun',
    description: 'White-sand beaches, turquoise Caribbean waters, all-inclusive resorts and Mayan ruins nearby.',
    destination: 'Cancun, Mexico',
    duration: '4–7 days',
    rating: 4.6,
    price: 'From $280',
    priceNum: 280,
    flightDuration: '~3h from Miami',
    bestFor: 'Beach · Resorts · Nightlife',
    bestSeason: 'Nov – Apr (dry & cooler)',
    highlights: ['Hotel Zone beach strip', 'Chichen Itza day trip', 'Cenote snorkeling'],
    tags: ['near-me', 'discount'],
    image: 'https://images.unsplash.com/photo-1773802352708-ede62ced59bb?crop=entropy&cs=srgb&fm=jpg&w=600&q=85',
    bookFlightUrl: 'https://www.google.com/travel/flights?q=flights+to+Cancun+Mexico',
    bookHotelUrl: 'https://www.booking.com/searchresults.html?ss=Cancun+Mexico',
  },
  {
    id: 6,
    name: 'Amalfi Coast',
    description: 'Dramatic cliffs, pastel villages, fresh seafood and some of the most scenic drives in the world.',
    destination: 'Amalfi Coast, Italy',
    duration: '5–8 days',
    rating: 4.8,
    price: 'From $1,000',
    priceNum: 1000,
    flightDuration: '~10h from NYC',
    bestFor: 'Romance · Scenery · Cuisine',
    bestSeason: 'May – Oct (warm & clear)',
    highlights: ['Positano cliff-side villages', 'Ravello gardens & views', 'Boat tours along the coast'],
    tags: ['popular', 'luxury'],
    image: 'https://images.pexels.com/photos/358223/pexels-photo-358223.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    bookFlightUrl: 'https://www.google.com/travel/flights?q=flights+to+Naples+Italy+Amalfi',
    bookHotelUrl: 'https://www.booking.com/searchresults.html?ss=Amalfi+Coast+Italy',
  },
  {
    id: 7,
    name: 'Dubai',
    description: 'Ultra-luxury shopping, record-breaking skyscrapers, desert safaris and world-class dining.',
    destination: 'Dubai, UAE',
    duration: '4–6 days',
    rating: 4.7,
    price: 'From $650',
    priceNum: 650,
    flightDuration: '~13h from NYC',
    bestFor: 'Luxury · Architecture · Shopping',
    bestSeason: 'Oct – Apr (cooler weather)',
    highlights: ['Burj Khalifa observation deck', 'Desert safari with dune bashing', 'Dubai Mall & indoor skiing'],
    tags: ['popular', 'luxury'],
    image: 'https://images.unsplash.com/photo-1768069794857-9306ac167c6e?crop=entropy&cs=srgb&fm=jpg&w=600&q=85',
    bookFlightUrl: 'https://www.google.com/travel/flights?q=flights+to+Dubai+UAE',
    bookHotelUrl: 'https://www.booking.com/searchresults.html?ss=Dubai+UAE',
  },
  {
    id: 8,
    name: 'Costa Rica',
    description: 'Pristine rainforests, erupting volcanoes, zip-lining and some of the best wildlife in the Americas.',
    destination: 'San José, Costa Rica',
    duration: '7–10 days',
    rating: 4.7,
    price: 'From $380',
    priceNum: 380,
    flightDuration: '~3h from Miami',
    bestFor: 'Adventure · Wildlife · Eco-Tourism',
    bestSeason: 'Dec – Apr (dry season)',
    highlights: ['Arenal Volcano hot springs', 'Manuel Antonio wildlife park', 'Zip-lining through cloud forest'],
    tags: ['near-me', 'discount'],
    image: 'https://images.pexels.com/photos/19938718/pexels-photo-19938718.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    bookFlightUrl: 'https://www.google.com/travel/flights?q=flights+to+San+Jose+Costa+Rica',
    bookHotelUrl: 'https://www.booking.com/searchresults.html?ss=Costa+Rica',
  },
  {
    id: 9,
    name: 'Lisbon',
    description: 'Charming tram rides through hilly streets, world-class pastries, and a buzzing art and food scene.',
    destination: 'Lisbon, Portugal',
    duration: '4–6 days',
    rating: 4.6,
    price: 'From $420',
    priceNum: 420,
    flightDuration: '~7h from NYC',
    bestFor: 'Culture · Food · Budget Travel',
    bestSeason: 'Mar – May · Sep – Oct',
    highlights: ['Alfama district & Fado music', 'Belém Tower & Pasteis de Nata', 'Sintra fairy-tale palaces day trip'],
    tags: ['discount'],
    image: 'https://images.pexels.com/photos/3763903/pexels-photo-3763903.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    bookFlightUrl: 'https://www.google.com/travel/flights?q=flights+to+Lisbon+Portugal',
    bookHotelUrl: 'https://www.booking.com/searchresults.html?ss=Lisbon+Portugal',
  },
  {
    id: 10,
    name: 'Phuket',
    description: 'Emerald waters, limestone karsts, vibrant street food markets and legendary island-hopping tours.',
    destination: 'Phuket, Thailand',
    duration: '7–10 days',
    rating: 4.7,
    price: 'From $680',
    priceNum: 680,
    flightDuration: '~22h from NYC',
    bestFor: 'Beaches · Budget · Nightlife',
    bestSeason: 'Nov – Apr (dry season)',
    highlights: ['Phi Phi Island boat day trip', 'Old Town street food tour', 'Phang Nga Bay sea kayak'],
    tags: ['discount'],
    image: 'https://images.pexels.com/photos/18277780/pexels-photo-18277780.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    bookFlightUrl: 'https://www.google.com/travel/flights?q=flights+to+Phuket+Thailand',
    bookHotelUrl: 'https://www.booking.com/searchresults.html?ss=Phuket+Thailand',
  },
  {
    id: 11,
    name: 'Bahamas',
    description: 'Turquoise waters, pink-sand beaches, swimming pigs at Exuma and seamless luxury resorts.',
    destination: 'Nassau, Bahamas',
    duration: '4–7 days',
    rating: 4.7,
    price: 'From $320',
    priceNum: 320,
    flightDuration: '~55 min from Miami',
    bestFor: 'Beach · Family · Luxury',
    bestSeason: 'Dec – Apr (peak season)',
    highlights: ['Exuma Cays swimming pigs', 'Atlantis Paradise Island resort', 'Blue Holes snorkeling'],
    tags: ['near-me', 'luxury'],
    image: 'https://images.pexels.com/photos/4784345/pexels-photo-4784345.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    bookFlightUrl: 'https://www.google.com/travel/flights?q=flights+to+Nassau+Bahamas',
    bookHotelUrl: 'https://www.booking.com/searchresults.html?ss=Nassau+Bahamas',
  },
  {
    id: 12,
    name: 'Paris',
    description: 'The City of Light — world-famous museums, haute cuisine, fashion and unmatched romantic ambiance.',
    destination: 'Paris, France',
    duration: '5–7 days',
    rating: 4.8,
    price: 'From $490',
    priceNum: 490,
    flightDuration: '~7h from NYC',
    bestFor: 'Romance · Art · Cuisine',
    bestSeason: 'Apr – Jun · Sep – Oct',
    highlights: ['Eiffel Tower at golden hour', 'Louvre & Musée d\'Orsay', 'Montmartre & Seine River cruise'],
    tags: ['popular'],
    image: 'https://images.pexels.com/photos/338515/pexels-photo-338515.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    bookFlightUrl: 'https://www.google.com/travel/flights?q=flights+to+Paris+France',
    bookHotelUrl: 'https://www.booking.com/searchresults.html?ss=Paris+France',
  },
];

const TRIP_CATEGORIES = [
  { id: 'cultural', name: 'Cultural', image: 'https://images.pexels.com/photos/36483523/pexels-photo-36483523.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=400' },
  { id: 'beaches', name: 'Beaches', image: 'https://images.pexels.com/photos/4784345/pexels-photo-4784345.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=400' },
  { id: 'nature', name: 'Nature', image: 'https://images.pexels.com/photos/6401371/pexels-photo-6401371.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=400' },
  { id: 'islands', name: 'Islands', image: 'https://images.pexels.com/photos/5718578/pexels-photo-5718578.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=400' },
  { id: 'unique', name: 'Unique', image: 'https://images.unsplash.com/photo-1618593157191-37c0d0f16c64?crop=entropy&cs=srgb&fm=jpg&w=400&q=80' },
];

const DONE_TRIPS = [
  { id: 1, name: 'Monas, Jakarta', dates: '6 Mar – 12 Mar (6 Days)', image: 'https://images.pexels.com/photos/36483523/pexels-photo-36483523.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=150' },
  { id: 2, name: 'Borobudur Temple, Yogya..', dates: '24 Feb – 29 Feb (5 Days)', image: 'https://images.pexels.com/photos/36675648/pexels-photo-36675648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=150' },
  { id: 3, name: 'Gili Islands, Lombok', dates: '12 Feb – 16 Feb (4 Days)', image: 'https://images.pexels.com/photos/4784345/pexels-photo-4784345.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=150' },
  { id: 4, name: 'Batu Cermin Cave, Labuan..', dates: '1 Feb – 8 Feb (8 Days)', image: 'https://images.pexels.com/photos/5718578/pexels-photo-5718578.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=150' },
];

const FRIENDS = [
  { id: 1, name: 'Alexander L.', location: 'Singapore', time: '01:24', badge: 3, initials: 'AL', color: 'bg-blue-500' },
  { id: 2, name: 'Maximilian H.', location: 'Japan', time: '12:24', badge: 2, initials: 'MH', color: 'bg-green-500' },
  { id: 3, name: 'Leonardo V.', location: 'Malaysia', time: '11:02', badge: null, initials: 'LV', color: 'bg-purple-500' },
  { id: 4, name: 'Isabella Sofia', location: 'Australia', time: '10:24', badge: null, initials: 'IS', color: 'bg-pink-500' },
];

const UPCOMING_TRIP = {
  name: 'Nusa Penida',
  desc: 'Nusa Penida is a breathtaking island southeast of Bali, famous for its dramatic cliffs, crystal-clear waters, and stunning beaches.',
  dates: '18 Mar – 22 Mar (4 Days)',
  hotel: '5 Star Hotel',
  origin: 'Seoul, Korea',
  destination: 'Bali, Indonesia',
  progress: 35,
  image: 'https://images.pexels.com/photos/5989716/pexels-photo-5989716.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=700',
  thumbs: [
    'https://images.pexels.com/photos/5718578/pexels-photo-5718578.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=150',
    'https://images.pexels.com/photos/4784345/pexels-photo-4784345.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=150',
    'https://images.unsplash.com/photo-1660207766758-a2e5985005ad?crop=entropy&cs=srgb&fm=jpg&w=200&q=70',
    'https://images.unsplash.com/photo-1618593157191-37c0d0f16c64?crop=entropy&cs=srgb&fm=jpg&w=200&q=70',
  ],
};

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const Travel = () => {
  const { user } = useAuth();
  const userId = user?.user_id || user?.email || '';
  const [mainTab, setMainTab] = useState('flights');
  const [exploreFilter, setExploreFilter] = useState('popular');
  const [activeCategory, setActiveCategory] = useState(null);
  const [heartedTrips, setHeartedTrips] = useState([]);

  // ---- My Trips state (persisted) ----
  const DEFAULT_MY_TRIPS = [
    {
      id: 'trip-1', name: 'Nusa Penida', destination: 'Bali, Indonesia', origin: 'Seoul, Korea',
      description: 'A breathtaking island southeast of Bali, famous for its dramatic cliffs, crystal-clear waters, and stunning beaches.',
      startDate: '2026-03-18', endDate: '2026-03-22', duration: '4 Days', hotel: '5 Star Hotel',
      status: 'upcoming', category: 'islands', rating: 4.9,
      image: 'https://images.pexels.com/photos/5989716/pexels-photo-5989716.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=700',
      itinerary: [
        { day: 1, title: 'Arrival & Check-in', activities: ['Arrive at Ngurah Rai Airport', 'Speed boat to Nusa Penida', 'Check in to resort', 'Sunset at Crystal Bay'] },
        { day: 2, title: 'Western Tour', activities: ["Kelingking Beach viewpoint", "Angel's Billabong", 'Broken Beach', 'Crystal Bay snorkeling'] },
        { day: 3, title: 'Eastern Tour', activities: ['Atuh Beach', 'Diamond Beach', 'Thousand Island Viewpoint', 'Local seafood dinner'] },
        { day: 4, title: 'Manta Snorkel & Departure', activities: ['Snorkeling at Manta Point', 'Sea turtle spotting', 'Speed boat back to Bali', 'Depart from Ngurah Rai'] },
      ],
      images: [
        'https://images.pexels.com/photos/5989716/pexels-photo-5989716.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=700',
        'https://images.pexels.com/photos/5718578/pexels-photo-5718578.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
      ],
      videos: [],
    },
    {
      id: 'trip-2', name: 'Monas, Jakarta', destination: 'Jakarta, Indonesia', origin: 'Home',
      description: 'Visited the National Monument of Indonesia and explored the old Batavia district with its rich colonial history.',
      startDate: '2026-03-06', endDate: '2026-03-12', duration: '6 Days', hotel: '4 Star Hotel',
      status: 'completed', category: 'cultural', rating: 4.5,
      image: 'https://images.pexels.com/photos/36483523/pexels-photo-36483523.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
      itinerary: [], images: [], videos: [],
    },
    {
      id: 'trip-3', name: 'Borobudur Temple, Yogya', destination: 'Yogyakarta, Indonesia', origin: 'Home',
      description: "Explored one of the world's greatest Buddhist monuments and its surrounding temples in the heart of Java.",
      startDate: '2026-02-24', endDate: '2026-02-29', duration: '5 Days', hotel: '3 Star Hotel',
      status: 'completed', category: 'cultural', rating: 4.8,
      image: 'https://images.pexels.com/photos/36675648/pexels-photo-36675648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
      itinerary: [], images: [], videos: [],
    },
    {
      id: 'trip-4', name: 'Gili Islands, Lombok', destination: 'Lombok, Indonesia', origin: 'Home',
      description: 'Snorkeling, diving, and relaxing on pristine white-sand beaches. No cars, just bicycles and horse-drawn carts.',
      startDate: '2026-02-12', endDate: '2026-02-16', duration: '4 Days', hotel: '4 Star Resort',
      status: 'completed', category: 'beaches', rating: 4.7,
      image: 'https://images.pexels.com/photos/4784345/pexels-photo-4784345.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
      itinerary: [], images: [], videos: [],
    },
    {
      id: 'trip-5', name: 'Batu Cermin Cave, Labuan', destination: 'Labuan Bajo, Indonesia', origin: 'Home',
      description: 'Discovered the mirror cave with its unique light reflections and explored the nearby Komodo National Park.',
      startDate: '2026-02-01', endDate: '2026-02-08', duration: '8 Days', hotel: '3 Star Hotel',
      status: 'completed', category: 'nature', rating: 4.6,
      image: 'https://images.pexels.com/photos/5718578/pexels-photo-5718578.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
      itinerary: [], images: [], videos: [],
    },
  ];

  const [myTrips, setMyTrips] = useState(() => {
    try { const s = localStorage.getItem('my_trips_v2'); return s ? JSON.parse(s) : DEFAULT_MY_TRIPS; } catch { return DEFAULT_MY_TRIPS; }
  });
  const [selectedTripDetail, setSelectedTripDetail] = useState(null);
  const [showTripDetail, setShowTripDetail] = useState(false);
  const [showAddTripModal, setShowAddTripModal] = useState(false);
  const [editingTripDetail, setEditingTripDetail] = useState(null);
  const [tripMenuOpen, setTripMenuOpen] = useState(null);
  const [tripFormData, setTripFormData] = useState({ name: '', destination: '', origin: '', description: '', startDate: '', endDate: '', hotel: '', status: 'upcoming', category: 'beaches', image: '' });
  // ---- Trips sub-views & detail tab ----
  const [tripsView, setTripsView] = useState(null); // 'explore' | 'upcoming' | 'done' | null
  const [detailTab, setDetailTab] = useState('overview');
  const [newItinDay, setNewItinDay] = useState({ title: '', activities: '' });
  const [editingItinIdx, setEditingItinIdx] = useState(null);
  const [editingItinData, setEditingItinData] = useState({ title: '', activities: '' });
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [filter, setFilter] = useState('all');
  const [trips, setTrips] = useState([]);
  const [flightCards, setFlightCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isBoardingPassOpen, setIsBoardingPassOpen] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [isFlightDialogOpen, setIsFlightDialogOpen] = useState(false);
  // When non-null, the flight dialog is in EDIT mode for this flight id
  const [editingFlightId, setEditingFlightId] = useState(null);
  const [flightFormData, setFlightFormData] = useState({
    airline: '',
    flight_number: '',
    departure: '',
    arrival: '',
    date: '',
    time: '',
    notes: ''
  });
  const [formData, setFormData] = useState({
    destination: '',
    start_date: '',
    end_date: '',
    purpose: '',
    status: 'planned',
    flight_details: '',
    hotel_details: '',
    notes: ''
  });

  // Default travel categories
  const defaultTravelCategories = [
    { id: 'all', name: 'All Trips', color: 'bg-white', icon: '📋' },
    { id: 'family', name: 'Family', color: 'bg-pink-500', icon: '👨‍👩‍👧‍👦' },
    { id: 'business', name: 'Business', color: 'bg-blue-500', icon: '💼' },
    { id: 'vacation', name: 'Vacation', color: 'bg-green-500', icon: '🏖️' },
    { id: 'personal', name: 'Personal', color: 'bg-purple-500', icon: '👤' }
  ];
  
  // Load custom travel categories from localStorage
  const [customTravelCategories, setCustomTravelCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('travel_categories');
      return saved ? JSON.parse(saved) : defaultTravelCategories;
    } catch { return defaultTravelCategories; }
  });
  
  // Editable category state
  const [showTravelCategoryModal, setShowTravelCategoryModal] = useState(false);
  const [newTravelCategoryName, setNewTravelCategoryName] = useState('');
  const [newTravelCategoryIcon, setNewTravelCategoryIcon] = useState('✈️');
  const [newTravelCategoryColor, setNewTravelCategoryColor] = useState('bg-blue-500');
  const [editingTravelCategory, setEditingTravelCategory] = useState(null);
  const [editTravelCategoryValue, setEditTravelCategoryValue] = useState('');
  
  // Available icons and colors
  const travelCategoryIcons = ['📋', '👨‍👩‍👧‍👦', '💼', '🏖️', '👤', '✈️', '🌍', '🏨', '🚗', '🚂', '🚢', '🎒', '🏔️', '🏝️', '🎡', '🎭', '⛷️', '🏕️', '🗺️', '📸'];
  const travelCategoryColors = ['bg-white', 'bg-pink-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-cyan-500', 'bg-red-500', 'bg-yellow-500', 'bg-indigo-500'];
  
  // Category management functions
  const addTravelCategory = () => {
    if (!newTravelCategoryName.trim()) return;
    const categoryId = newTravelCategoryName.toLowerCase().replace(/\s+/g, '-');
    if (customTravelCategories.some(c => c.id === categoryId)) {
      toast.error('Category already exists');
      return;
    }
    const newCategory = { id: categoryId, name: newTravelCategoryName, color: newTravelCategoryColor, icon: newTravelCategoryIcon };
    const updated = [...customTravelCategories, newCategory];
    setCustomTravelCategories(updated);
    localStorage.setItem('travel_categories', JSON.stringify(updated));
    setNewTravelCategoryName('');
    setNewTravelCategoryIcon('✈️');
    setNewTravelCategoryColor('bg-blue-500');
    toast.success(`Added "${newTravelCategoryName}" category`);
  };
  
  const updateTravelCategory = (oldId, newName) => {
    if (!newName.trim()) return;
    const newId = newName.toLowerCase().replace(/\s+/g, '-');
    const updated = customTravelCategories.map(c => c.id === oldId ? { ...c, id: newId, name: newName } : c);
    setCustomTravelCategories(updated);
    localStorage.setItem('travel_categories', JSON.stringify(updated));
    if (filter === oldId) setFilter(newId);
    setEditingTravelCategory(null);
    setEditTravelCategoryValue('');
    toast.success(`Category renamed to "${newName}"`);
  };
  
  const deleteTravelCategory = (categoryId) => {
    if (['all', 'business', 'personal'].includes(categoryId)) {
      toast.error('Cannot delete default category');
      return;
    }
    const updated = customTravelCategories.filter(c => c.id !== categoryId);
    setCustomTravelCategories(updated);
    localStorage.setItem('travel_categories', JSON.stringify(updated));
    if (filter === categoryId) setFilter('all');
    toast.success('Category deleted');
  };

  // Use custom categories
  const categories = customTravelCategories;

  useEffect(() => {
    fetchTravelEvents();
  }, [userId]);

  // Fetch travel events from calendar
  const fetchTravelEvents = async () => {
    try {
      setLoading(true);

      let allFlights = [];

      // ----- 1) Calendar-derived flights via the server-side parser -----
      // The backend pulls every synced calendar event, filters to
      // travel-flavored ones, and extracts structured fields (airline,
      // flight #, route, gate, seat, terminal, confirmation, etc.).
      if (userId) {
        try {
          const response = await fetch(`${API_URL}/api/flights/from-calendar?user_id=${encodeURIComponent(userId)}`);
          if (response.ok) {
            const data = await response.json();
            const parsed = Array.isArray(data.flights) ? data.flights : [];
            const calendarFlights = parsed.map(f => {
              const startDate = f.start ? new Date(f.start) : new Date();
              const valid = !isNaN(startDate.getTime());
              return {
                id: f.id,
                type: f.type || 'outbound',
                airline: f.airline,
                airlineCode: f.airline_code || 'XX',
                flightNumber: f.flight_number || '',
                departure: f.departure || '',
                arrival: f.arrival || '',
                date: valid ? startDate : new Date(),
                dateFormatted: valid
                  ? startDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                  : '',
                time: valid ? startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '',
                gate: f.gate || '',
                seat: f.seat || '',
                terminal: f.terminal || '',
                confirmationCode: f.confirmation_code || '',
                subject: f.subject || '',
                status: valid && startDate > new Date() ? 'upcoming' : 'completed',
                source: 'calendar',
              };
            });
            allFlights = [...allFlights, ...calendarFlights];
          }
        } catch (err) {
          console.log('Calendar flight parser unavailable:', err);
        }
      }
      
      // Filter out deleted flights from database
      try {
        const deletedResponse = await fetch(`${API_URL}/api/flights/deleted`);
        if (deletedResponse.ok) {
          const deletedData = await deletedResponse.json();
          const deletedFlights = deletedData.deleted_flights || [];
          allFlights = allFlights.filter(flight => !deletedFlights.includes(flight.id));
        }
      } catch (err) {
        console.log('Could not fetch deleted flights from API');
      }
      
      // ALWAYS load manually added flights from database
      if (userId) {
        try {
          const manualRes = await fetch(`${API_URL}/api/flights/manual?user_id=${encodeURIComponent(userId)}`);
          if (manualRes.ok) {
            const manualData = await manualRes.json();
            const manualFlights = (manualData.flights || []).map(f => ({
              id: f.id,
              type: 'outbound',
              airline: f.airline,
              airlineCode: f.airline?.substring(0, 2)?.toUpperCase() || 'XX',
              flightNumber: f.flight_number || '',
              departure: f.departure,
              arrival: f.arrival,
              date: new Date(f.date),
              dateFormatted: new Date(f.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
              time: f.time || '',
              subject: f.notes || `${f.airline} ${f.departure} → ${f.arrival}`,
              status: f.status || 'upcoming',
              source: 'manual',
              // Boarding pass + parsed BCBP fields (populated after upload)
              boardingPassFileId: f.boarding_pass_file_id || null,
              boardingPassUploadedAt: f.boarding_pass_uploaded_at || null,
              boardingPassFilename: f.boarding_pass_filename || null,
              seat: f.seat || (f.boarding_pass_bcbp && f.boarding_pass_bcbp.seat) || '',
              gate: f.gate || '',
              terminal: f.terminal || '',
              passengerName: f.passenger_name || (f.boarding_pass_bcbp && f.boarding_pass_bcbp.passenger_name) || '',
              pnr: f.pnr || (f.boarding_pass_bcbp && f.boarding_pass_bcbp.pnr) || '',
              confirmationCode: f.confirmation_code || f.pnr || '',
            }));
            allFlights = [...allFlights, ...manualFlights];
            console.log(`Loaded ${manualFlights.length} manual flights from database`);
          }
        } catch (err) {
          console.error('Could not fetch manual flights:', err);
        }
      }
      
      // ---- Quality filter + dedup ----
      // 1) Drop garbage cards: must have at least an airline (not "Unknown")
      //    OR a flight number OR both an origin+destination route.
      // 2) Dedup by signature (flight number + departure + arrival + same-day),
      //    preferring the entry with the most complete info (more populated fields).
      const isUsable = (f) => {
        const hasAirline = !!(f.airline && f.airline !== 'Unknown Airline' && f.airline !== 'XX');
        const hasFlightNum = !!(f.flightNumber && f.flightNumber.replace(/\s+/g, '').length >= 2);
        const hasRoute = !!(f.departure && f.arrival && f.departure !== '' && f.arrival !== '' && f.arrival !== 'TBD');
        return hasAirline || hasFlightNum || hasRoute;
      };
      const completeness = (f) => {
        let n = 0;
        if (f.airline && f.airline !== 'Unknown Airline' && f.airline !== 'XX') n++;
        if (f.flightNumber) n++;
        if (f.departure) n++;
        if (f.arrival && f.arrival !== 'TBD') n++;
        if (f.time) n++;
        if (f.gate) n++;
        if (f.seat) n++;
        if (f.confirmationCode) n++;
        return n;
      };
      const sigFor = (f) => {
        const dayKey = (f.date instanceof Date && !isNaN(f.date.getTime()))
          ? f.date.toISOString().split('T')[0]
          : '';
        // Prefer flight number when present, otherwise fall back to route+day
        const fn = (f.flightNumber || '').toUpperCase().replace(/\s+/g, '');
        if (fn) return `fn:${fn}:${dayKey}`;
        return `rt:${(f.departure || '').toUpperCase()}:${(f.arrival || '').toUpperCase()}:${dayKey}`;
      };
      const cleaned = allFlights.filter(isUsable);
      const bestBySig = new Map();
      cleaned.forEach(f => {
        const sig = sigFor(f);
        const existing = bestBySig.get(sig);
        if (!existing || completeness(f) > completeness(existing)) {
          bestBySig.set(sig, f);
        }
      });
      allFlights = Array.from(bestBySig.values());

      // Sort all flights by date
      allFlights.sort((a, b) => a.date - b.date);
      setFlightCards(allFlights);
      
      // Create trip cards from grouped flights
      const tripGroups = {};
      allFlights.forEach(flight => {
        const monthKey = `${flight.date.getFullYear()}-${flight.date.getMonth()}`;
        if (!tripGroups[monthKey]) {
          tripGroups[monthKey] = [];
        }
        tripGroups[monthKey].push(flight);
      });
      
      const tripCards = Object.entries(tripGroups).map(([key, groupFlights]) => ({
        id: key,
        destination: groupFlights[0]?.arrival || 'Trip',
        start_date: groupFlights[0]?.date.toISOString().split('T')[0],
        end_date: groupFlights[groupFlights.length - 1]?.date.toISOString().split('T')[0],
        flights: groupFlights,
        category: 'business',
        status: 'planned'
      }));
      
      // Filter out deleted trips from localStorage
      const deletedTrips = JSON.parse(localStorage.getItem('deleted_trips') || '[]');
      const filteredTrips = tripCards.filter(trip => !deletedTrips.includes(trip.id));
      
      setTrips(filteredTrips);
      
    } catch (error) {
      console.error('Error fetching travel events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create new trip using API with userId
      const result = await travelAPI.create(formData, userId);
      const newTrip = result.data || result;
      setTrips([newTrip, ...trips]);
      setIsDialogOpen(false);
      setFormData({
        destination: '',
        start_date: '',
        end_date: '',
        purpose: '',
        status: 'planned',
        flight_details: '',
        hotel_details: '',
        notes: ''
      });
      toast.success('Trip created successfully!');
    } catch (error) {
      console.error('Error creating trip:', error);
      toast.error('Failed to create trip. Please try again.');
    }
  };

  // Open the flight dialog in EDIT mode. Works for both manual flights
  // (writes to /api/flights/manual/:id) and calendar-sourced flights
  // (writes to /api/flights/calendar/:id as user overrides).
  const handleEditFlightCard = (flight, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!flight) return;
    // Normalize the date back to YYYY-MM-DD for the <input type="date">
    let dateStr = '';
    if (flight.date instanceof Date && !isNaN(flight.date.getTime())) {
      const y = flight.date.getFullYear();
      const m = String(flight.date.getMonth() + 1).padStart(2, '0');
      const d = String(flight.date.getDate()).padStart(2, '0');
      dateStr = `${y}-${m}-${d}`;
    } else if (typeof flight.date === 'string') {
      dateStr = flight.date.split('T')[0];
    }
    setFlightFormData({
      airline: flight.airline || '',
      flight_number: flight.flightNumber || flight.flight_number || '',
      departure: flight.departure || '',
      arrival: flight.arrival || '',
      date: dateStr,
      time: flight.time || '',
      notes: flight.subject || flight.notes || '',
    });
    setEditingFlightId(flight.id);
    setIsFlightDialogOpen(true);
  };

  // Add OR update a flight depending on editingFlightId.
  const handleAddFlight = async (e) => {
    e.preventDefault();
    if (!flightFormData.airline || !flightFormData.departure || !flightFormData.arrival || !flightFormData.date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // -------- EDIT path --------
      if (editingFlightId) {
        // Determine source — calendar vs manual — and route to the right endpoint
        const target = flightCards.find(c => c.id === editingFlightId);
        const isCalendar = target?.source === 'calendar';
        const endpoint = isCalendar
          ? `${API_URL}/api/flights/calendar/${encodeURIComponent(editingFlightId)}`
          : `${API_URL}/api/flights/manual/${encodeURIComponent(editingFlightId)}`;

        // Calendar uses 'start' ISO instead of date+time
        let body;
        if (isCalendar) {
          let startIso = null;
          if (flightFormData.date) {
            const t = flightFormData.time || '09:00';
            startIso = `${flightFormData.date}T${t.length === 5 ? t + ':00' : t}`;
          }
          body = {
            airline: flightFormData.airline,
            airline_code: (flightFormData.airline || '').substring(0, 2).toUpperCase() || undefined,
            flight_number: flightFormData.flight_number,
            departure: flightFormData.departure,
            arrival: flightFormData.arrival,
            ...(startIso ? { start: startIso } : {}),
          };
        } else {
          body = flightFormData;
        }

        const response = await fetch(endpoint, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (response.ok) {
          const data = await response.json();
          const f = isCalendar ? (data.overrides || {}) : (data.flight || {});
          setFlightCards(prev => prev.map(card => {
            if (card.id !== editingFlightId) return card;
            const newAirline = f.airline || card.airline;
            const newDate = isCalendar
              ? (f.start ? new Date(f.start) : card.date)
              : (f.date ? new Date(f.date) : card.date);
            return {
              ...card,
              airline: newAirline,
              airlineCode: (f.airline_code || newAirline || '').substring(0, 2).toUpperCase() || card.airlineCode,
              flightNumber: (f.flight_number ?? card.flightNumber) || '',
              departure: f.departure || card.departure,
              arrival: f.arrival || card.arrival,
              date: newDate,
              dateFormatted: newDate && !isNaN(newDate.getTime())
                ? newDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                : card.dateFormatted,
              time: isCalendar
                ? (f.start ? new Date(f.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : card.time)
                : (f.time ?? card.time),
              subject: isCalendar ? card.subject : (f.notes ?? card.subject),
              status: f.status || card.status,
            };
          }).sort((a, b) => a.date - b.date));
          setIsFlightDialogOpen(false);
          setEditingFlightId(null);
          setFlightFormData({
            airline: '', flight_number: '', departure: '', arrival: '', date: '', time: '', notes: ''
          });
          toast.success(isCalendar ? 'Flight updated (calendar override saved)' : 'Flight updated');
        } else {
          toast.error('Failed to update flight');
        }
        return;
      }

      // -------- CREATE path --------
      const response = await fetch(`${API_URL}/api/flights/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...flightFormData,
          user_id: userId
        })
      });

      if (response.ok) {
        const data = await response.json();
        const newFlight = {
          id: data.flight.id,
          type: 'outbound',
          airline: data.flight.airline,
          airlineCode: data.flight.airline?.substring(0, 2)?.toUpperCase() || 'XX',
          flightNumber: data.flight.flight_number || '',
          departure: data.flight.departure,
          arrival: data.flight.arrival,
          date: new Date(data.flight.date),
          dateFormatted: new Date(data.flight.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
          time: data.flight.time || '',
          subject: data.flight.notes || `${data.flight.airline} ${data.flight.departure} → ${data.flight.arrival}`,
          status: data.flight.status || 'upcoming',
          source: 'manual'
        };
        
        // Add to flight cards and sort by date
        setFlightCards(prev => [...prev, newFlight].sort((a, b) => a.date - b.date));
        setIsFlightDialogOpen(false);
        setFlightFormData({
          airline: '',
          flight_number: '',
          departure: '',
          arrival: '',
          date: '',
          time: '',
          notes: ''
        });
        toast.success('Flight added successfully!');
      } else {
        toast.error('Failed to add flight');
      }
    } catch (error) {
      console.error('Error adding flight:', error);
      toast.error('Failed to add flight');
    }
  };

  const handleEditTrip = (trip) => {
    setEditingTrip(trip);
    setFormData({
      destination: trip.destination || '',
      start_date: trip.start_date || '',
      end_date: trip.end_date || '',
      purpose: trip.purpose || '',
      status: trip.status || 'planned',
      flight_details: trip.flight_details || '',
      hotel_details: trip.hotel_details || '',
      notes: trip.notes || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleDeleteTrip = async (tripId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this trip?')) {
      try {
        // Delete trip from API
        await travelAPI.delete(tripId);
        const updatedTrips = trips.filter(trip => trip.id !== tripId);
        setTrips(updatedTrips);
        toast.success('Trip deleted');
      } catch (error) {
        console.error('Error deleting trip:', error);
        toast.error('Failed to delete trip');
      }
    }
  };

  const handleViewDetails = (trip) => {
    setSelectedTrip(trip);
    setIsDetailsDialogOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingTrip) return;

    try {
      // Update trip via API
      await travelAPI.update(editingTrip.id, formData);
      const updatedTrips = trips.map(trip =>
        trip.id === editingTrip.id
          ? { ...trip, ...formData }
          : trip
      );
      setTrips(updatedTrips);
      setIsEditDialogOpen(false);
      setEditingTrip(null);
      setFormData({
        destination: '',
        start_date: '',
        end_date: '',
        purpose: '',
        status: 'planned',
        flight_details: '',
        hotel_details: '',
        notes: ''
      });
      toast.success('Trip updated successfully!');
    } catch (error) {
      console.error('Error updating trip:', error);
      toast.error('Failed to update trip. Please try again.');
    }
  };

  // Delete a flight card
  const handleDeleteFlightCard = async (flightId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this flight?')) {
      try {
        // Find the flight to check its source
        const flight = flightCards.find(f => f.id === flightId);
        
        if (flight?.source === 'manual') {
          // Delete manual flight from database permanently
          const response = await fetch(`${API_URL}/api/flights/manual/${flightId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
          });
          
          if (response.ok) {
            const updatedFlights = flightCards.filter(f => f.id !== flightId);
            setFlightCards(updatedFlights);
            toast.success('Flight deleted permanently');
            console.log(`Manual flight ${flightId} deleted permanently from database`);
          } else {
            console.error('Failed to delete manual flight from database');
            toast.error('Failed to delete flight. Please try again.');
          }
        } else {
          // Calendar-synced flight — hide via flight_overrides flag on the event doc
          const response = await fetch(`${API_URL}/api/flights/calendar/${encodeURIComponent(flightId)}`, {
            method: 'DELETE',
          });
          
          if (response.ok) {
            const updatedFlights = flightCards.filter(f => f.id !== flightId);
            setFlightCards(updatedFlights);
            toast.success('Flight hidden from Travel page');
            console.log(`Calendar flight ${flightId} marked as hidden`);
          } else {
            console.error('Failed to hide calendar flight');
            toast.error('Failed to delete flight. Please try again.');
          }
        }
      } catch (error) {
        console.error('Error deleting flight:', error);
        toast.error('Failed to delete flight. Please try again.');
      }
    }
  };

  // Open boarding pass modal — shows the REAL boarding pass image if the
  // user has uploaded one, otherwise just shows the structured flight info
  // (no fake QR/seat/gate placeholders).
  const openBoardingPass = (flight) => {
    setSelectedFlight(flight);
    setIsBoardingPassOpen(true);
  };

  // Upload a real boarding pass file. Backend decodes the QR/PDF417 and
  // auto-fills seat/PNR/passenger name when found.
  const handleUploadBoardingPass = async (flight, file) => {
    if (!flight || !file) return;
    if (flight.source !== 'manual') {
      toast.info('Save this flight first (or convert from calendar) before attaching a boarding pass.');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/flights/manual/${encodeURIComponent(flight.id)}/boarding-pass`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        console.error('Boarding pass upload failed:', res.status, t);
        let parsed = t;
        try { const j = JSON.parse(t); parsed = j.detail || t; } catch (_e) { /* leave as text */ }
        toast.error(`Upload failed (${res.status}): ${String(parsed).slice(0, 140) || 'Unknown error'}`);
        return;
      }
      const data = await res.json();
      const f = data.flight || {};
      const codesFound = data.codes_found || 0;
      // Refresh just this card
      setFlightCards(prev => prev.map(card => {
        if (card.id !== flight.id) return card;
        return {
          ...card,
          flightNumber: f.flight_number || card.flightNumber,
          seat: f.seat || (f.boarding_pass_bcbp && f.boarding_pass_bcbp.seat) || card.seat,
          passengerName: f.passenger_name || (f.boarding_pass_bcbp && f.boarding_pass_bcbp.passenger_name) || card.passengerName,
          pnr: f.pnr || (f.boarding_pass_bcbp && f.boarding_pass_bcbp.pnr) || card.pnr,
          boardingPassFileId: f.boarding_pass_file_id || card.boardingPassFileId,
          boardingPassUploadedAt: f.boarding_pass_uploaded_at || card.boardingPassUploadedAt,
          boardingPassFilename: f.boarding_pass_filename || card.boardingPassFilename,
        };
      }));
      toast.success(codesFound > 0 ? `Boarding pass attached (${codesFound} barcode${codesFound > 1 ? 's' : ''} decoded)` : 'Boarding pass attached');
    } catch (err) {
      console.error('Boarding pass upload error:', err);
      toast.error(`Boarding pass upload failed: ${err?.message || 'network error'}`);
    }
  };

  // Remove an uploaded boarding pass
  const handleRemoveBoardingPass = async (flight, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!flight || flight.source !== 'manual') return;
    if (!window.confirm('Remove the uploaded boarding pass?')) return;
    try {
      const res = await fetch(`${API_URL}/api/flights/manual/${encodeURIComponent(flight.id)}/boarding-pass`, { method: 'DELETE' });
      if (res.ok) {
        setFlightCards(prev => prev.map(card => card.id === flight.id ? { ...card, boardingPassFileId: null, boardingPassUploadedAt: null, boardingPassFilename: null } : card));
        toast.success('Boarding pass removed');
      } else {
        toast.error('Failed to remove boarding pass');
      }
    } catch (err) {
      toast.error('Failed to remove boarding pass');
    }
  };

  // ---- My Trips handlers ----
  const saveMyTrips = (trips) => { setMyTrips(trips); localStorage.setItem('my_trips_v2', JSON.stringify(trips)); };

  const handleOpenTripDetail = (trip) => { setSelectedTripDetail(trip); setShowTripDetail(true); };

  const handleOpenAddMyTrip = () => {
    setTripFormData({ name: '', destination: '', origin: '', description: '', startDate: '', endDate: '', hotel: '', status: 'upcoming', category: 'beaches', image: '' });
    setEditingTripDetail(null);
    setShowAddTripModal(true);
  };

  const handleEditMyTrip = (trip, e) => {
    if (e) e.stopPropagation();
    setTripFormData({ name: trip.name, destination: trip.destination, origin: trip.origin || '', description: trip.description || '', startDate: trip.startDate || '', endDate: trip.endDate || '', hotel: trip.hotel || '', status: trip.status || 'upcoming', category: trip.category || 'beaches', image: trip.image || '' });
    setEditingTripDetail(trip);
    setShowTripDetail(false);
    setShowAddTripModal(true);
    setTripMenuOpen(null);
  };

  const handleSaveMyTrip = () => {
    if (!tripFormData.name.trim() || !tripFormData.destination.trim()) { toast.error('Trip name and destination are required'); return; }
    const duration = tripFormData.startDate && tripFormData.endDate
      ? `${Math.max(1, Math.round((new Date(tripFormData.endDate) - new Date(tripFormData.startDate)) / 86400000))} Days`
      : '';
    if (editingTripDetail) {
      const updated = myTrips.map(t => t.id === editingTripDetail.id ? { ...t, ...tripFormData, duration } : t);
      saveMyTrips(updated);
      toast.success('Trip updated!');
    } else {
      saveMyTrips([{ id: `trip-${Date.now()}`, ...tripFormData, duration, rating: null }, ...myTrips]);
      toast.success('Trip added!');
    }
    setShowAddTripModal(false);
    setEditingTripDetail(null);
  };

  const handleDeleteMyTrip = (tripId, e) => {
    if (e) e.stopPropagation();
    saveMyTrips(myTrips.filter(t => t.id !== tripId));
    setTripMenuOpen(null);
    if (selectedTripDetail?.id === tripId) setShowTripDetail(false);
    toast.success('Trip removed');
  };

  // --- Helpers ---
  const getYouTubeId = (url) => { const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/); return m ? m[1] : null; };

  const updateTrip = (id, patch) => {
    const updated = myTrips.map(t => t.id === id ? { ...t, ...patch } : t);
    saveMyTrips(updated);
    setSelectedTripDetail(prev => prev?.id === id ? { ...prev, ...patch } : prev);
  };

  // Itinerary handlers
  const handleAddItinDay = (tripId) => {
    if (!newItinDay.title.trim()) return;
    const trip = myTrips.find(t => t.id === tripId);
    const itin = [...(trip?.itinerary || []), {
      day: (trip?.itinerary?.length || 0) + 1,
      title: newItinDay.title,
      activities: newItinDay.activities.split('\n').map(a => a.trim()).filter(Boolean),
    }];
    updateTrip(tripId, { itinerary: itin });
    setNewItinDay({ title: '', activities: '' });
    toast.success('Day added');
  };

  const handleDeleteItinDay = (tripId, idx) => {
    const trip = myTrips.find(t => t.id === tripId);
    const itin = (trip?.itinerary || []).filter((_, i) => i !== idx).map((d, i) => ({ ...d, day: i + 1 }));
    updateTrip(tripId, { itinerary: itin });
  };

  const handleSaveItinEdit = (tripId) => {
    const trip = myTrips.find(t => t.id === tripId);
    const itin = (trip?.itinerary || []).map((d, i) => i === editingItinIdx
      ? { ...d, title: editingItinData.title, activities: editingItinData.activities.split('\n').map(a => a.trim()).filter(Boolean) }
      : d
    );
    updateTrip(tripId, { itinerary: itin });
    setEditingItinIdx(null);
    toast.success('Day updated');
  };

  // Image handlers
  const handleAddImage = (tripId) => {
    if (!newImageUrl.trim()) return;
    const trip = myTrips.find(t => t.id === tripId);
    updateTrip(tripId, { images: [...(trip?.images || []), newImageUrl.trim()] });
    setNewImageUrl('');
    toast.success('Image added');
  };

  const handleDeleteImage = (tripId, idx) => {
    const trip = myTrips.find(t => t.id === tripId);
    updateTrip(tripId, { images: (trip?.images || []).filter((_, i) => i !== idx) });
  };

  // Video handlers
  const handleAddVideo = (tripId) => {
    if (!newVideoUrl.trim()) return;
    const trip = myTrips.find(t => t.id === tripId);
    updateTrip(tripId, { videos: [...(trip?.videos || []), { url: newVideoUrl.trim(), title: newVideoTitle.trim() || 'Video' }] });
    setNewVideoUrl('');
    setNewVideoTitle('');
    toast.success('Video added');
  };

  const handleDeleteVideo = (tripId, idx) => {
    const trip = myTrips.find(t => t.id === tripId);
    updateTrip(tripId, { videos: (trip?.videos || []).filter((_, i) => i !== idx) });
  };

  const filteredTrips = filter === 'all' ? trips : trips.filter(t => t.category === filter);
  const filteredFlights = filter === 'all' ? flightCards : flightCards.filter(f => {
    // Map flight types to categories
    if (filter === 'business') return true; // All flights could be business
    if (filter === 'vacation') return f.subject?.toLowerCase().includes('vacation');
    if (filter === 'family') return f.subject?.toLowerCase().includes('family');
    if (filter === 'personal') return f.subject?.toLowerCase().includes('personal');
    return true;
  });

  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Travel</h1>
          <p className="text-gray-400">Manage your flights, trips and destinations</p>
        </div>
        <div className="flex gap-3">
          {mainTab === 'flights' && (
            <>
              <Button 
                className="bg-blue-500 hover:bg-blue-600 text-white"
                onClick={() => setIsFlightDialogOpen(true)}
              >
                <Plane size={18} className="mr-2" />
                Add Flight
              </Button>
              <Button 
                className="bg-red-500 hover:bg-red-600 text-white"
                onClick={() => setIsDialogOpen(true)}
              >
                <Plus size={18} className="mr-2" />
                Add Trip
              </Button>
              <Button
                variant="outline"
                className="border-amber-700 text-amber-300 hover:bg-amber-900/30 hover:text-amber-200"
                onClick={async () => {
                  try {
                    const r = await fetch(`${API_URL}/api/flights/manual/cleanup${userId ? `?user_id=${encodeURIComponent(userId)}` : ''}`, { method: 'POST' });
                    if (!r.ok) {
                      toast.error('Cleanup failed');
                      return;
                    }
                    const j = await r.json();
                    if (j.deleted > 0) {
                      toast.success(`Removed ${j.deleted} incomplete flight card${j.deleted === 1 ? '' : 's'}`);
                      fetchTravelEvents();
                    } else {
                      toast.info('No bad flight cards found — you\'re clean.');
                    }
                  } catch (err) {
                    toast.error('Cleanup failed');
                  }
                }}
                data-testid="cleanup-flights-btn"
              >
                <Trash2 size={18} className="mr-2" />
                Clean Up
              </Button>
            </>
          )}
          {mainTab === 'trips' && (
            <Button
              data-testid="add-my-trip-btn"
              className="bg-white text-black hover:bg-gray-200 font-semibold"
              onClick={handleOpenAddMyTrip}
            >
              <Plus size={18} className="mr-2" />
              Add Trip
            </Button>
          )}
        </div>
      </div>

      {/* Main Tab Bar */}
      <div className="flex gap-1 mb-6 bg-black/40 border border-white/20 rounded-xl p-1 w-fit">
        {[
          { id: 'flights', label: 'Flights', icon: Plane },
          { id: 'trips', label: 'Trips', icon: Globe },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setMainTab(tab.id)}
            data-testid={`main-tab-${tab.id}`}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
              mainTab === tab.id
                ? 'bg-white text-black shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ==================== FLIGHTS TAB ==================== */}
      {mainTab === 'flights' && (
        <div>

      {/* Category Filters */}
      <div className="mb-6 bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-4">
        <div className="flex flex-wrap gap-2 items-center">
          {categories.map((category) => {
            const count = category.id === 'all' ? flightCards.length : flightCards.filter(f => {
              if (category.id === 'business') return true;
              if (category.id === 'vacation') return f.subject?.toLowerCase().includes('vacation');
              if (category.id === 'family') return f.subject?.toLowerCase().includes('family');
              if (category.id === 'personal') return f.subject?.toLowerCase().includes('personal');
              return false;
            }).length;
            return (
              <button
                key={category.id}
                onClick={() => setFilter(category.id)}
                data-testid={`travel-category-${category.id}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  filter === category.id
                    ? category.id === 'all' ? 'bg-white text-black' : `${category.color} text-white`
                    : 'bg-black text-gray-400 hover:text-white'
                }`}
              >
                <span>{category.icon}</span>
                {category.name} ({count})
              </button>
            );
          })}
          
          {/* Edit Categories Button */}
          <button
            onClick={() => setShowTravelCategoryModal(true)}
            data-testid="edit-travel-categories-btn"
            className="px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1"
            title="Edit Categories"
          >
            <Pencil size={14} />
            Edit
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-6">
          <div className="text-4xl font-bold text-cyan-500 mb-2">{flightCards.length}</div>
          <div className="text-gray-400 text-sm">Total Trips</div>
        </div>
        <div className="bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-6">
          <div className="text-4xl font-bold text-orange-500 mb-2">
            {flightCards.filter(f => f.status === 'upcoming').length}
          </div>
          <div className="text-gray-400 text-sm">Upcoming</div>
        </div>
        <div className="bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-6">
          <div className="text-4xl font-bold text-green-500 mb-2">
            {flightCards.filter(f => f.status === 'completed').length}
          </div>
          <div className="text-gray-400 text-sm">Completed</div>
        </div>
        <div className="bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-6">
          <div className="text-4xl font-bold text-purple-500 mb-2">
            {flightCards.filter(f => f.status === 'cancelled').length || 0}
          </div>
          <div className="text-gray-400 text-sm">Cancelled</div>
        </div>
      </div>

      {/* Flight Cards from Calendar Events */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
          <Plane size={24} className="text-blue-400" />
          Upcoming Flights
        </h2>
        
        {filteredFlights.length === 0 ? (
          <div className="bg-black border border-gray-600 rounded-xl p-8 text-center">
            <Plane size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 mb-2">No flight events found in your calendar</p>
            <p className="text-gray-500 text-sm">Add travel events to your Outlook calendar to see them here</p>
            <p className="text-gray-500 text-xs mt-2">Example: "AA Flight DFW to SNA" or "Return Flight - AA SNA to DFW"</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFlights.map((flight) => (
              <div 
                key={flight.id}
                onClick={() => openBoardingPass(flight)}
                className={`bg-black border-2 rounded-xl p-5 hover:shadow-lg transition-all relative group cursor-pointer hover:scale-[1.02] ${
                  flight.type === 'return' ? 'border-orange-500' : 'border-blue-500'
                }`}
              >
                {/* Edit + Delete Buttons — always visible, top-right corner, z-10 so badges never cover them */}
                <button
                  onClick={(e) => handleEditFlightCard(flight, e)}
                  data-testid={`edit-flight-${flight.id}`}
                  className="absolute top-3 right-12 z-10 p-2 bg-gray-900/80 backdrop-blur text-gray-300 hover:text-blue-400 hover:bg-blue-500/20 rounded-lg transition-all border border-gray-700/50"
                  title="Edit flight"
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={(e) => handleDeleteFlightCard(flight.id, e)}
                  data-testid={`delete-flight-${flight.id}`}
                  className="absolute top-3 right-3 z-10 p-2 bg-gray-900/80 backdrop-blur text-gray-300 hover:text-red-500 hover:bg-red-500/20 rounded-lg transition-all border border-gray-700/50"
                  title="Delete flight"
                >
                  <Trash2 size={16} />
                </button>

                {/* Flight Type + Status Badges (reserve space on right for action buttons) */}
                <div className="flex items-center gap-2 mb-3 pr-24 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    flight.type === 'return' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {flight.type === 'return' ? '↩ Return Flight' : '✈ Outbound Flight'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    flight.status === 'upcoming' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {flight.status === 'upcoming' ? 'Upcoming' : 'Completed'}
                  </span>
                </div>
                
                {/* Airline Info */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                    <span className="text-black font-bold text-sm">{flight.airlineCode}</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold">{flight.airline}</p>
                    <p className="text-gray-400 text-xs">{flight.flightNumber}</p>
                  </div>
                </div>
                
                {/* Route */}
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center min-w-0 flex-shrink-0">
                    <p className="text-3xl font-bold text-white leading-none">{flight.departure}</p>
                    {getAirportCity(flight.departure) && (
                      <p className="text-[11px] text-gray-300 mt-1 font-medium truncate max-w-[110px]" data-testid={`departure-city-${flight.id}`}>
                        {getAirportCity(flight.departure)}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Departure</p>
                  </div>
                  <div className="flex-1 flex items-center justify-center px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <div className="h-0.5 w-12 bg-gradient-to-r from-blue-400 to-orange-400"></div>
                      <Plane size={16} className="text-white" />
                      <div className="h-0.5 w-12 bg-gradient-to-r from-orange-400 to-blue-400"></div>
                      <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    </div>
                  </div>
                  <div className="text-center min-w-0 flex-shrink-0">
                    <p className="text-3xl font-bold text-white leading-none">{flight.arrival}</p>
                    {getAirportCity(flight.arrival) && (
                      <p className="text-[11px] text-gray-300 mt-1 font-medium truncate max-w-[110px]" data-testid={`arrival-city-${flight.id}`}>
                        {getAirportCity(flight.arrival)}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Arrival</p>
                  </div>
                </div>
                
                {/* Date & Time */}
                <div className="bg-gray-900 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-cyan-400" />
                    <span className="text-white text-sm font-medium">{flight.dateFormatted}</span>
                  </div>
                  {flight.time && (
                    <div className="flex items-center gap-1">
                      <Clock size={14} className="text-gray-400" />
                      <span className="text-gray-300 text-sm">{formatFlightTime12h(flight.time)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Flight Tracker Section */}
      <FlightTracker />
      </div>
      )}{/* end flights tab */}

      {/* ==================== TRIPS TAB ==================== */}
      {mainTab === 'trips' && (
        <div>
        {/* ---- SEE ALL VIEWS ---- */}
        {tripsView && (
          <div>
            <div className="flex items-center gap-4 mb-6">
              <button
                data-testid="trips-back-btn"
                onClick={() => setTripsView(null)}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors px-3 py-1.5 border border-white/20 rounded-xl hover:border-white/40"
              >
                <ChevronRight size={16} className="rotate-180" /> Back
              </button>
              <h2 className="text-2xl font-bold text-white capitalize">
                {tripsView === 'explore' ? 'All Explore Destinations' : tripsView === 'upcoming' ? 'All Upcoming Trips' : 'All Done Trips'}
              </h2>
              {tripsView !== 'explore' && (
                <button onClick={handleOpenAddMyTrip} className="ml-auto flex items-center gap-2 px-4 py-1.5 bg-white text-black font-semibold text-sm rounded-xl hover:bg-gray-200 transition-colors">
                  <Plus size={15} /> Add Trip
                </button>
              )}
            </div>

            {tripsView === 'explore' && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {EXPLORE_TRIPS.map(trip => (
                  <div
                    key={trip.id}
                    onClick={() => handleOpenTripDetail({ ...trip, status: 'explore', origin: 'Your Location' })}
                    className="relative rounded-xl overflow-hidden group cursor-pointer border border-white/10 hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.08)] transition-all duration-300"
                  >
                    <img src={trip.image} alt={trip.name} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/70 backdrop-blur-sm rounded-full text-green-400 text-[11px] font-bold border border-green-500/30">{trip.price}</div>
                    <button onClick={(e) => { e.stopPropagation(); setHeartedTrips(prev => prev.includes(trip.id) ? prev.filter(id => id !== trip.id) : [...prev, trip.id]); }} className="absolute top-2 right-2 w-8 h-8 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/60 transition-colors z-10">
                      <Heart size={14} className={heartedTrips.includes(trip.id) ? 'text-red-500 fill-red-500' : 'text-white'} />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-3">
                      <p className="font-bold text-white text-sm">{trip.name}</p>
                      <p className="text-gray-400 text-[11px] mt-0.5 line-clamp-1">{trip.bestFor}</p>
                      <div className="flex items-center justify-between mt-1.5">
                        <div className="flex items-center gap-2 text-xs text-gray-300">
                          <span className="flex items-center gap-1"><Clock size={10} /> {trip.flightDuration}</span>
                          <span className="flex items-center gap-1"><Star size={10} className="text-yellow-400 fill-yellow-400" /> {trip.rating}</span>
                        </div>
                        <a href={trip.bookFlightUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="px-2 py-0.5 bg-white text-black text-[10px] font-bold rounded-full hover:bg-gray-200 transition-colors">Book</a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(tripsView === 'upcoming' || tripsView === 'done') && (
              <>
                {myTrips.filter(t => tripsView === 'upcoming' ? t.status === 'upcoming' : t.status === 'completed').length === 0 ? (
                  <div className="text-center py-16 border border-dashed border-white/20 rounded-2xl">
                    <Globe size={40} className="text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No {tripsView} trips yet</p>
                    <button onClick={handleOpenAddMyTrip} className="mt-4 text-sm text-white border border-white/30 px-5 py-2 rounded-full hover:bg-white/5 transition-colors">+ Add a Trip</button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {myTrips.filter(t => tripsView === 'upcoming' ? t.status === 'upcoming' : t.status === 'completed').map(trip => (
                      <div
                        key={trip.id}
                        data-testid={`seeall-trip-card-${trip.id}`}
                        onClick={() => { handleOpenTripDetail(trip); setTripsView(null); }}
                        className="bg-transparent border border-white/20 rounded-2xl overflow-hidden hover:border-white/50 hover:shadow-[0_0_25px_rgba(255,255,255,0.06)] transition-all duration-300 cursor-pointer group"
                      >
                        <div className="relative h-44 overflow-hidden">
                          <img src={trip.image || 'https://images.pexels.com/photos/5989716/pexels-photo-5989716.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600'} alt={trip.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <span className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-semibold ${trip.status === 'upcoming' ? 'bg-blue-500/80' : 'bg-green-500/80'} text-white`}>{trip.status}</span>
                        </div>
                        <div className="p-4">
                          <h3 className="font-bold text-white">{trip.name}</h3>
                          <p className="text-gray-400 text-sm mt-1 line-clamp-2">{trip.description}</p>
                          <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><MapPin size={11} /> {trip.destination}</span>
                            {trip.duration && <span className="flex items-center gap-1"><Calendar size={11} /> {trip.duration}</span>}
                          </div>
                          <div className="flex items-center gap-2 mt-3">
                            <button onClick={(e) => { e.stopPropagation(); handleEditMyTrip(trip, e); }} className="flex-1 py-1.5 text-xs border border-white/20 text-gray-300 rounded-lg hover:bg-white/5 hover:border-white/40 transition-colors flex items-center justify-center gap-1"><Edit2 size={12} /> Edit</button>
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteMyTrip(trip.id, e); }} className="px-3 py-1.5 text-xs border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/10 transition-colors flex items-center gap-1"><Trash2 size={12} /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ---- MAIN TRIPS GRID (hidden when see-all is active) ---- */}
        {!tripsView && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

          {/* ---- LEFT / MAIN COLUMN ---- */}
          <div className="xl:col-span-2 space-y-6">

            {/* EXPLORE TRIPS */}
            <div className="bg-transparent border border-white/60 rounded-2xl p-5 hover:border-white/80 hover:shadow-[0_0_25px_rgba(255,255,255,0.06)] transition-all duration-300">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h2 className="text-xl font-bold text-white">Explore Trips</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  {['popular', 'near-me', 'discount'].map(f => (
                    <button
                      key={f}
                      data-testid={`explore-filter-${f}`}
                      onClick={() => setExploreFilter(f)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                        exploreFilter === f
                          ? 'bg-white text-black border-white'
                          : 'bg-transparent border-white/30 text-gray-400 hover:border-white/60 hover:text-white'
                      }`}
                    >
                      {f === 'near-me' ? 'Near Me' : f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                  <button className="px-3 py-1 rounded-full text-xs font-semibold border border-white/30 text-gray-400 hover:text-white hover:border-white/60 transition-all flex items-center gap-1">
                    <Filter size={11} /> Filter
                  </button>
                  <button
                    data-testid="explore-see-all-btn"
                    onClick={() => setTripsView('explore')}
                    className="text-xs text-gray-400 hover:text-white transition-colors flex items-center gap-1"
                  >
                    See All <ChevronRight size={14} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {EXPLORE_TRIPS.filter(trip =>
                  exploreFilter === 'popular' ? trip.tags.includes('popular') :
                  exploreFilter === 'near-me' ? trip.tags.includes('near-me') :
                  exploreFilter === 'discount' ? trip.tags.includes('discount') : true
                ).slice(0, 4).map(trip => (
                  <div
                    key={trip.id}
                    data-testid={`explore-trip-card-${trip.id}`}
                    onClick={() => handleOpenTripDetail({ ...trip, status: 'explore', origin: 'Your Location', startDate: '', endDate: '', hotel: '' })}
                    className="relative rounded-xl overflow-hidden group cursor-pointer border border-white/10 hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.08)] transition-all duration-300"
                  >
                    <img src={trip.image} alt={trip.name} className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500" />
                    {/* Price badge */}
                    <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/70 backdrop-blur-sm rounded-full text-green-400 text-[11px] font-bold border border-green-500/30">
                      {trip.price}
                    </div>
                    <button
                      data-testid={`heart-trip-${trip.id}`}
                      onClick={(e) => { e.stopPropagation(); setHeartedTrips(prev => prev.includes(trip.id) ? prev.filter(id => id !== trip.id) : [...prev, trip.id]); }}
                      className="absolute top-2 right-2 w-8 h-8 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/60 transition-colors z-10"
                    >
                      <Heart size={14} className={heartedTrips.includes(trip.id) ? 'text-red-500 fill-red-500' : 'text-white'} />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-3">
                      <p className="font-bold text-white text-sm leading-tight">{trip.name}</p>
                      <p className="text-gray-300 text-[11px] mt-0.5 line-clamp-1">{trip.bestFor}</p>
                      <div className="flex items-center justify-between mt-1.5">
                        <div className="flex items-center gap-2 text-xs text-gray-300">
                          <span className="flex items-center gap-1"><Clock size={10} /> {trip.flightDuration}</span>
                          <span className="flex items-center gap-1"><Star size={10} className="text-yellow-400 fill-yellow-400" /> {trip.rating}</span>
                        </div>
                        <a
                          href={trip.bookFlightUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="px-2 py-0.5 bg-white text-black text-[10px] font-bold rounded-full hover:bg-gray-200 transition-colors"
                        >
                          Book
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CATEGORY BUTTONS */}
            <div className="bg-transparent border border-white/60 rounded-2xl p-4 hover:border-white/80 hover:shadow-[0_0_25px_rgba(255,255,255,0.06)] transition-all duration-300">
              <div className="grid grid-cols-5 gap-3">
                {TRIP_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    data-testid={`category-btn-${cat.id}`}
                    onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
                    className={`relative rounded-xl overflow-hidden aspect-square border transition-all duration-300 ${
                      activeCategory === cat.id ? 'border-white/80 shadow-[0_0_20px_rgba(255,255,255,0.15)]' : 'border-white/10 hover:border-white/40'
                    }`}
                  >
                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20 flex items-end justify-center pb-2">
                      <span className="text-white text-xs font-bold">{cat.name}</span>
                    </div>
                    {activeCategory === cat.id && (
                      <div className="absolute inset-0 ring-2 ring-white rounded-xl" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* UPCOMING TRIPS */}
            <div className="bg-transparent border border-white/60 rounded-2xl p-5 hover:border-white/80 hover:shadow-[0_0_25px_rgba(255,255,255,0.06)] transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Upcoming Trips</h2>
                <div className="flex items-center gap-2">
                  <button data-testid="add-upcoming-trip-btn" onClick={handleOpenAddMyTrip} className="text-xs text-gray-400 hover:text-white transition-colors flex items-center gap-1 px-2 py-1 border border-white/20 rounded-lg hover:border-white/40">
                    <Plus size={12} /> Add
                  </button>
                  <button data-testid="upcoming-see-all-btn" onClick={() => setTripsView('upcoming')} className="text-xs text-gray-400 hover:text-white transition-colors flex items-center gap-1">
                    See All <ChevronRight size={14} />
                  </button>
                </div>
              </div>

              {myTrips.filter(t => t.status === 'upcoming').length === 0 ? (
                <div className="text-center py-10 border border-dashed border-white/20 rounded-xl">
                  <Globe size={32} className="text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">No upcoming trips yet</p>
                  <button onClick={handleOpenAddMyTrip} className="mt-3 text-xs text-white border border-white/30 px-4 py-1.5 rounded-full hover:bg-white/5 transition-colors">+ Plan a Trip</button>
                </div>
              ) : (
                <div className="space-y-3">
                  {myTrips.filter(t => t.status === 'upcoming').map(trip => (
                    <div
                      key={trip.id}
                      data-testid={`upcoming-trip-card-${trip.id}`}
                      onClick={() => handleOpenTripDetail(trip)}
                      className="bg-black/30 border border-white/15 rounded-xl overflow-hidden hover:border-white/35 transition-all duration-300 cursor-pointer group"
                    >
                      <div className="flex flex-col md:flex-row">
                        <div className="md:w-2/5 flex-shrink-0 relative">
                          <img src={trip.image || 'https://images.pexels.com/photos/5989716/pexels-photo-5989716.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=700'} alt={trip.name} className="w-full h-48 object-cover" />
                          <div className="flex gap-1.5 p-2 bg-black/20">
                            {[trip.image].filter(Boolean).map((thumb, i) => (
                              <img key={i} src={thumb} alt="" className="w-12 h-10 rounded-lg object-cover border border-white/10 hover:border-white/40 transition-colors cursor-pointer" />
                            ))}
                          </div>
                        </div>
                        <div className="flex-1 p-4 relative">
                          <div className="flex items-start justify-between">
                            <h3 className="text-lg font-bold text-white">{trip.name}</h3>
                            <div className="relative">
                              <button
                                data-testid={`trip-menu-btn-${trip.id}`}
                                onClick={(e) => { e.stopPropagation(); setTripMenuOpen(tripMenuOpen === trip.id ? null : trip.id); }}
                                className="p-1 text-gray-500 hover:text-white transition-colors"
                              >
                                <MoreHorizontal size={18} />
                              </button>
                              {tripMenuOpen === trip.id && (
                                <div className="absolute right-0 top-8 bg-gray-900 border border-white/20 rounded-xl shadow-xl z-20 min-w-[140px] overflow-hidden">
                                  <button data-testid={`edit-trip-btn-${trip.id}`} onClick={(e) => handleEditMyTrip(trip, e)} className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"><Edit2 size={14} /> Edit Trip</button>
                                  <button data-testid={`delete-trip-btn-${trip.id}`} onClick={(e) => handleDeleteMyTrip(trip.id, e)} className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-white/10 flex items-center gap-2 transition-colors"><Trash2 size={14} /> Delete</button>
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-400 mt-2 leading-relaxed line-clamp-2">{trip.description}</p>
                          <div className="flex items-center gap-5 mt-3 text-sm text-gray-300 flex-wrap">
                            {trip.startDate && <span className="flex items-center gap-1.5"><Calendar size={14} className="text-gray-400" /> {trip.startDate} – {trip.endDate} {trip.duration && `(${trip.duration})`}</span>}
                            {trip.hotel && <span className="flex items-center gap-1.5"><Hotel size={14} className="text-gray-400" /> {trip.hotel}</span>}
                          </div>
                          {trip.origin && trip.destination && (
                            <div className="flex items-center gap-3 mt-4">
                              <span className="text-xs text-gray-500 whitespace-nowrap">{trip.origin}</span>
                              <div className="flex-1 relative h-1.5 bg-white/10 rounded-full">
                                <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-white/30 rounded-full" style={{ width: '35%' }} />
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-black" />
                                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-2.5 h-2.5 bg-white/40 rounded-full border-2 border-black" />
                              </div>
                              <span className="text-xs text-gray-500 whitespace-nowrap">{trip.destination}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>{/* end left column */}

          {/* ---- RIGHT COLUMN ---- */}
          <div className="space-y-6">

            {/* FRIENDS */}
            <div className="bg-transparent border border-white/60 rounded-2xl p-5 hover:border-white/80 hover:shadow-[0_0_25px_rgba(255,255,255,0.06)] transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Friends</h2>
                <button className="text-xs text-gray-400 hover:text-white transition-colors flex items-center gap-1">
                  See All <ChevronRight size={14} />
                </button>
              </div>
              <div className="space-y-3">
                {FRIENDS.map(friend => (
                  <div key={friend.id} data-testid={`friend-item-${friend.id}`} className="flex items-center justify-between group p-2 rounded-xl hover:bg-white/5 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full ${friend.color} flex items-center justify-center text-white font-bold text-sm flex-shrink-0 relative`}>
                        {friend.initials}
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-black" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-semibold">{friend.name}</p>
                        <p className="text-gray-500 text-xs">{friend.location}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{friend.time}</span>
                      {friend.badge && (
                        <span className="w-5 h-5 bg-green-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {friend.badge}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* DONE TRIPS */}
            <div className="bg-transparent border border-white/60 rounded-2xl p-5 hover:border-white/80 hover:shadow-[0_0_25px_rgba(255,255,255,0.06)] transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Done Trips</h2>
                <button data-testid="done-see-all-btn" onClick={() => setTripsView('done')} className="text-xs text-gray-400 hover:text-white transition-colors flex items-center gap-1">
                  See All <ChevronRight size={14} />
                </button>
              </div>
              <div className="space-y-3">
                {myTrips.filter(t => t.status === 'completed').length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No completed trips yet</p>
                ) : (
                  myTrips.filter(t => t.status === 'completed').map(trip => (
                    <div
                      key={trip.id}
                      data-testid={`done-trip-card-${trip.id}`}
                      onClick={() => handleOpenTripDetail(trip)}
                      className="flex items-center justify-between group p-2 rounded-xl hover:bg-white/5 transition-colors cursor-pointer border border-transparent hover:border-white/15"
                    >
                      <div className="flex items-center gap-3">
                        <img src={trip.image || 'https://images.pexels.com/photos/4784345/pexels-photo-4784345.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=150'} alt={trip.name} className="w-16 h-12 rounded-lg object-cover flex-shrink-0 border border-white/10" />
                        <div>
                          <p className="text-white text-sm font-semibold line-clamp-1">{trip.name}</p>
                          <p className="text-gray-500 text-xs flex items-center gap-1 mt-0.5">
                            <Calendar size={11} />
                            {trip.startDate && trip.endDate ? `${trip.startDate} – ${trip.endDate} (${trip.duration})` : trip.duration || 'No dates'}
                          </p>
                        </div>
                      </div>
                      <div className="relative">
                        <button
                          data-testid={`done-trip-menu-${trip.id}`}
                          onClick={(e) => { e.stopPropagation(); setTripMenuOpen(tripMenuOpen === trip.id ? null : trip.id); }}
                          className="p-1 text-gray-600 hover:text-white transition-colors"
                        >
                          <MoreHorizontal size={16} />
                        </button>
                        {tripMenuOpen === trip.id && (
                          <div className="absolute right-0 top-8 bg-gray-900 border border-white/20 rounded-xl shadow-xl z-20 min-w-[140px] overflow-hidden">
                            <button onClick={(e) => handleEditMyTrip(trip, e)} className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"><Edit2 size={14} /> Edit Trip</button>
                            <button onClick={(e) => handleDeleteMyTrip(trip.id, e)} className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-white/10 flex items-center gap-2 transition-colors"><Trash2 size={14} /> Delete</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

          </div>{/* end right column */}
        </div>
        )}{/* end main grid */}
        </div>
      )}{/* end trips tab */}

      {/* ====== TRIP DETAIL MODAL ====== */}
      {showTripDetail && selectedTripDetail && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { setShowTripDetail(false); setDetailTab('overview'); }}>
          <div className="bg-[#0a0a0a] border border-white/25 rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>

            {/* Hero */}
            <div className="relative h-52 flex-shrink-0 overflow-hidden rounded-t-2xl">
              <img src={selectedTripDetail.image || 'https://images.pexels.com/photos/5989716/pexels-photo-5989716.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=700'} alt={selectedTripDetail.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <button data-testid="close-trip-detail-btn" onClick={() => { setShowTripDetail(false); setDetailTab('overview'); }} className="absolute top-4 right-4 w-9 h-9 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/80 transition-colors border border-white/20">
                <X size={18} />
              </button>
              <div className="absolute bottom-0 left-0 p-5 flex items-end justify-between w-full">
                <div>
                  {selectedTripDetail.category && <span className="px-2.5 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs text-white border border-white/20 capitalize">{selectedTripDetail.category}</span>}
                  <h2 className="text-2xl font-bold text-white mt-2">{selectedTripDetail.name}</h2>
                  {selectedTripDetail.destination && <p className="text-gray-300 text-sm flex items-center gap-1 mt-1"><MapPin size={13} /> {selectedTripDetail.destination}</p>}
                </div>
                {selectedTripDetail.status !== 'explore' && (
                  <button data-testid="trip-detail-edit-btn" onClick={() => handleEditMyTrip(selectedTripDetail, null)} className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white text-sm font-semibold rounded-xl hover:bg-white/20 transition-colors flex items-center gap-2">
                    <Edit2 size={14} /> Edit
                  </button>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-5 pt-4 pb-0 flex-shrink-0 border-b border-white/10">
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'itinerary', label: 'Itinerary' },
                { id: 'gallery', label: 'Gallery' },
                { id: 'videos', label: 'Videos' },
              ].map(tab => (
                <button
                  key={tab.id}
                  data-testid={`detail-tab-${tab.id}`}
                  onClick={() => setDetailTab(tab.id)}
                  className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-all -mb-px ${detailTab === tab.id ? 'bg-[#0a0a0a] border-t border-l border-r border-white/20 text-white' : 'text-gray-500 hover:text-gray-300'}`}
                >
                  {tab.label}
                  {tab.id === 'itinerary' && selectedTripDetail.itinerary?.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 bg-white/10 text-xs rounded-full">{selectedTripDetail.itinerary.length}</span>}
                  {tab.id === 'gallery' && selectedTripDetail.images?.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 bg-white/10 text-xs rounded-full">{selectedTripDetail.images.length}</span>}
                  {tab.id === 'videos' && selectedTripDetail.videos?.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 bg-white/10 text-xs rounded-full">{selectedTripDetail.videos.length}</span>}
                </button>
              ))}
            </div>

            {/* Tab content — scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">

              {/* OVERVIEW TAB */}
              {detailTab === 'overview' && (
                <div className="space-y-4">
                  {selectedTripDetail.description && <p className="text-gray-300 leading-relaxed">{selectedTripDetail.description}</p>}

                  {/* Explore-specific booking panel */}
                  {selectedTripDetail.status === 'explore' && (
                    <div className="space-y-3">
                      {/* Key stats row */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3 text-center">
                          <p className="text-xs text-gray-500 mb-1">Est. Price</p>
                          <p className="text-green-400 font-bold text-base">{selectedTripDetail.price}</p>
                        </div>
                        <div className="bg-black/40 border border-white/10 rounded-xl p-3 text-center">
                          <p className="text-xs text-gray-500 mb-1">Flight</p>
                          <p className="text-white font-semibold text-sm">{selectedTripDetail.flightDuration}</p>
                        </div>
                        <div className="bg-black/40 border border-white/10 rounded-xl p-3 text-center">
                          <p className="text-xs text-gray-500 mb-1">Duration</p>
                          <p className="text-white font-semibold text-sm">{selectedTripDetail.duration}</p>
                        </div>
                      </div>

                      {/* Best season */}
                      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3">
                        <p className="text-xs text-yellow-500 font-semibold mb-0.5">Best Time to Visit</p>
                        <p className="text-white text-sm">{selectedTripDetail.bestSeason}</p>
                      </div>

                      {/* Best for */}
                      <div className="bg-black/40 border border-white/10 rounded-xl p-3">
                        <p className="text-xs text-gray-500 mb-1.5">Perfect For</p>
                        <div className="flex flex-wrap gap-1.5">
                          {(selectedTripDetail.bestFor || '').split(' · ').map((tag, i) => (
                            <span key={i} className="px-2.5 py-1 bg-white/10 rounded-full text-xs text-white">{tag}</span>
                          ))}
                        </div>
                      </div>

                      {/* Highlights */}
                      {selectedTripDetail.highlights?.length > 0 && (
                        <div className="bg-black/40 border border-white/10 rounded-xl p-3">
                          <p className="text-xs text-gray-500 mb-2">Highlights</p>
                          <ul className="space-y-1.5">
                            {selectedTripDetail.highlights.map((h, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-gray-200">
                                <span className="text-green-400 mt-0.5 flex-shrink-0">✓</span>
                                {h}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Booking CTA buttons */}
                      <div className="grid grid-cols-2 gap-3 pt-1">
                        <a
                          href={selectedTripDetail.bookFlightUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 px-4 py-3 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition-colors text-sm"
                        >
                          <Plane size={15} />
                          Book Flights
                        </a>
                        <a
                          href={selectedTripDetail.bookHotelUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors text-sm"
                        >
                          <MapPin size={15} />
                          Find Hotels
                        </a>
                      </div>
                      <p className="text-center text-xs text-gray-600">Opens Google Flights & Booking.com with your destination pre-filled</p>
                    </div>
                  )}

                  {/* My trips details */}
                  {selectedTripDetail.status !== 'explore' && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        {selectedTripDetail.startDate && selectedTripDetail.endDate && (
                          <div className="bg-black/40 border border-white/10 rounded-xl p-3"><p className="text-xs text-gray-500 mb-1">Dates</p><p className="text-white font-semibold text-sm">{selectedTripDetail.startDate} → {selectedTripDetail.endDate}</p></div>
                        )}
                        {selectedTripDetail.duration && (
                          <div className="bg-black/40 border border-white/10 rounded-xl p-3"><p className="text-xs text-gray-500 mb-1">Duration</p><p className="text-white font-semibold text-sm">{selectedTripDetail.duration}</p></div>
                        )}
                        {selectedTripDetail.hotel && (
                          <div className="bg-black/40 border border-white/10 rounded-xl p-3"><p className="text-xs text-gray-500 mb-1">Accommodation</p><p className="text-white font-semibold text-sm">{selectedTripDetail.hotel}</p></div>
                        )}
                        {selectedTripDetail.status && (
                          <div className="bg-black/40 border border-white/10 rounded-xl p-3">
                            <p className="text-xs text-gray-500 mb-1">Status</p>
                            <span className={`text-sm font-semibold capitalize px-2 py-0.5 rounded-full ${selectedTripDetail.status === 'upcoming' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : selectedTripDetail.status === 'completed' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'}`}>{selectedTripDetail.status}</span>
                          </div>
                        )}
                      </div>
                      {selectedTripDetail.origin && selectedTripDetail.destination && (
                        <div className="bg-black/40 border border-white/10 rounded-xl p-4">
                          <p className="text-xs text-gray-500 mb-3">Route</p>
                          <div className="flex items-center gap-3">
                            <div className="text-center"><p className="text-white font-bold text-sm">{selectedTripDetail.origin}</p><p className="text-gray-500 text-xs">Origin</p></div>
                            <div className="flex-1 relative flex items-center"><div className="flex-1 h-px bg-white/20" /><Plane size={16} className="text-white mx-2 flex-shrink-0" /><div className="flex-1 h-px bg-white/20" /></div>
                            <div className="text-center"><p className="text-white font-bold text-sm">{selectedTripDetail.destination}</p><p className="text-gray-500 text-xs">Destination</p></div>
                          </div>
                        </div>
                      )}
                      {selectedTripDetail.rating && (
                        <div className="flex items-center gap-2">
                          {[...Array(5)].map((_, i) => <Star key={i} size={18} className={i < Math.floor(selectedTripDetail.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'} />)}
                          <span className="text-white font-semibold ml-1">{selectedTripDetail.rating}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ITINERARY TAB */}
              {detailTab === 'itinerary' && selectedTripDetail.status !== 'explore' && (
                <div className="space-y-4">
                  {(!selectedTripDetail.itinerary || selectedTripDetail.itinerary.length === 0) && (
                    <div className="text-center py-8 border border-dashed border-white/20 rounded-xl">
                      <Calendar size={28} className="text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm">No itinerary yet. Add your first day below.</p>
                    </div>
                  )}
                  {(selectedTripDetail.itinerary || []).map((day, idx) => (
                    <div key={idx} className="bg-black/30 border border-white/10 rounded-xl p-4 hover:border-white/25 transition-colors">
                      {editingItinIdx === idx ? (
                        <div className="space-y-2">
                          <input value={editingItinData.title} onChange={e => setEditingItinData({ ...editingItinData, title: e.target.value })} placeholder="Day title" className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/50" />
                          <textarea value={editingItinData.activities} onChange={e => setEditingItinData({ ...editingItinData, activities: e.target.value })} placeholder="Activities (one per line)" rows={4} className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/50 resize-none" />
                          <div className="flex gap-2">
                            <button onClick={() => handleSaveItinEdit(selectedTripDetail.id)} className="flex-1 py-1.5 bg-white text-black text-sm font-semibold rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"><Check size={14} /> Save</button>
                            <button onClick={() => setEditingItinIdx(null)} className="px-4 py-1.5 border border-white/20 text-gray-300 text-sm rounded-lg hover:bg-white/5 transition-colors">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-start justify-between">
                            <div>
                              <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Day {day.day}</span>
                              <h4 className="text-white font-semibold mt-0.5">{day.title}</h4>
                            </div>
                            <div className="flex gap-1">
                              <button data-testid={`edit-itin-day-${idx}`} onClick={() => { setEditingItinIdx(idx); setEditingItinData({ title: day.title, activities: (day.activities || []).join('\n') }); }} className="p-1.5 text-gray-500 hover:text-white transition-colors rounded-lg hover:bg-white/5"><Edit2 size={13} /></button>
                              <button data-testid={`delete-itin-day-${idx}`} onClick={() => handleDeleteItinDay(selectedTripDetail.id, idx)} className="p-1.5 text-gray-500 hover:text-red-400 transition-colors rounded-lg hover:bg-white/5"><Trash2 size={13} /></button>
                            </div>
                          </div>
                          {day.activities && day.activities.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {day.activities.map((act, ai) => <li key={ai} className="text-gray-400 text-sm flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-white/30 mt-2 flex-shrink-0" />{act}</li>)}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add day form */}
                  <div className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-2">
                    <p className="text-sm font-semibold text-gray-300">+ Add New Day</p>
                    <input data-testid="new-itin-title" value={newItinDay.title} onChange={e => setNewItinDay({ ...newItinDay, title: e.target.value })} placeholder="Day title (e.g. Arrival & Beach)" className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-white/50" />
                    <textarea data-testid="new-itin-activities" value={newItinDay.activities} onChange={e => setNewItinDay({ ...newItinDay, activities: e.target.value })} placeholder="Activities (one per line)&#10;e.g. Visit the beach&#10;Check in to hotel" rows={3} className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-white/50 resize-none" />
                    <button data-testid="add-itin-day-btn" onClick={() => handleAddItinDay(selectedTripDetail.id)} className="w-full py-2 bg-white text-black text-sm font-semibold rounded-lg hover:bg-gray-200 transition-colors">Add Day</button>
                  </div>
                </div>
              )}
              {/* ITINERARY TAB */}
              {detailTab === 'itinerary' && selectedTripDetail.status === 'explore' && (
                <div className="space-y-3">
                  <p className="text-gray-400 text-sm">Sample itinerary — customize once you book your trip.</p>
                  {(selectedTripDetail.highlights || []).map((h, i) => (
                    <div key={i} className="bg-black/40 border border-white/10 rounded-xl p-4 flex items-start gap-3">
                      <span className="flex-shrink-0 w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-sm font-bold text-white">Day {i + 1}</span>
                      <div>
                        <p className="text-white font-medium text-sm">{h}</p>
                        <p className="text-gray-500 text-xs mt-0.5">Suggested activity · adjust to your schedule</p>
                      </div>
                    </div>
                  ))}
                  <div className="mt-2 pt-2 border-t border-white/10">
                    <a href={selectedTripDetail.bookFlightUrl} target="_blank" rel="noopener noreferrer" className="block w-full text-center py-3 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition-colors text-sm">
                      Book This Trip on Google Flights
                    </a>
                  </div>
                </div>
              )}

              {/* GALLERY TAB */}
              {detailTab === 'gallery' && (
                <div className="space-y-4">
                  {(!selectedTripDetail.images || selectedTripDetail.images.length === 0) ? (
                    <div className="text-center py-8 border border-dashed border-white/20 rounded-xl">
                      <div className="w-10 h-10 mx-auto mb-2 text-gray-600 flex items-center justify-center"><MapPin size={28} /></div>
                      <p className="text-gray-400 text-sm">No images yet. Add a photo URL below.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {(selectedTripDetail.images || []).map((img, idx) => (
                        <div key={idx} className="relative group rounded-xl overflow-hidden aspect-video border border-white/10 hover:border-white/30 transition-colors">
                          <img src={img} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" onError={e => { e.target.src = 'https://images.pexels.com/photos/5989716/pexels-photo-5989716.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=300'; }} />
                          {selectedTripDetail.status !== 'explore' && (
                            <button data-testid={`delete-image-${idx}`} onClick={() => handleDeleteImage(selectedTripDetail.id, idx)} className="absolute top-2 right-2 w-7 h-7 bg-black/70 rounded-full flex items-center justify-center text-red-400 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/90">
                              <X size={13} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedTripDetail.status !== 'explore' && (
                    <div className="bg-black/20 border border-white/10 rounded-xl p-4 flex gap-2">
                      <input data-testid="new-image-url" type="url" value={newImageUrl} onChange={e => setNewImageUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddImage(selectedTripDetail.id)} placeholder="Paste image URL..." className="flex-1 bg-black border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-white/50" />
                      <button data-testid="add-image-btn" onClick={() => handleAddImage(selectedTripDetail.id)} className="px-4 py-2 bg-white text-black text-sm font-semibold rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap">Add Photo</button>
                    </div>
                  )}
                </div>
              )}

              {/* VIDEOS TAB */}
              {detailTab === 'videos' && (
                <div className="space-y-4">
                  {(!selectedTripDetail.videos || selectedTripDetail.videos.length === 0) ? (
                    <div className="text-center py-8 border border-dashed border-white/20 rounded-xl">
                      <p className="text-gray-400 text-sm">No videos yet. Add a YouTube or video URL below.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(selectedTripDetail.videos || []).map((video, idx) => {
                        const ytId = getYouTubeId(video.url);
                        return (
                          <div key={idx} className="bg-black/30 border border-white/10 rounded-xl overflow-hidden hover:border-white/25 transition-colors group">
                            {ytId ? (
                              <div className="relative">
                                <img src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`} alt={video.title} className="w-full h-44 object-cover" />
                                <a href={video.url} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/60 transition-colors">
                                  <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center shadow-xl"><svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-white ml-1"><polygon points="5,3 19,12 5,21" /></svg></div>
                                </a>
                              </div>
                            ) : (
                              <a href={video.url} target="_blank" rel="noopener noreferrer" className="block p-3 text-blue-400 hover:text-blue-300 transition-colors text-sm truncate">{video.url}</a>
                            )}
                            <div className="p-3 flex items-center justify-between">
                              <p className="text-white text-sm font-semibold">{video.title}</p>
                              {selectedTripDetail.status !== 'explore' && (
                                <button data-testid={`delete-video-${idx}`} onClick={() => handleDeleteVideo(selectedTripDetail.id, idx)} className="p-1.5 text-gray-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {selectedTripDetail.status !== 'explore' && (
                    <div className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-2">
                      <p className="text-sm font-semibold text-gray-300">+ Add Video</p>
                      <input data-testid="new-video-url" type="url" value={newVideoUrl} onChange={e => setNewVideoUrl(e.target.value)} placeholder="YouTube or video URL..." className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-white/50" />
                      <input data-testid="new-video-title" value={newVideoTitle} onChange={e => setNewVideoTitle(e.target.value)} placeholder="Video title (optional)" className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-white/50" />
                      <button data-testid="add-video-btn" onClick={() => handleAddVideo(selectedTripDetail.id)} className="w-full py-2 bg-white text-black text-sm font-semibold rounded-lg hover:bg-gray-200 transition-colors">Add Video</button>
                    </div>
                  )}
                </div>
              )}

            </div>{/* end scrollable content */}

            <div className="px-6 pb-5 pt-3 flex gap-3 flex-shrink-0 border-t border-white/10">
              <button data-testid="trip-detail-close-btn" onClick={() => { setShowTripDetail(false); setDetailTab('overview'); }} className="flex-1 py-2.5 border border-white/30 text-white font-semibold rounded-xl hover:bg-white/5 transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ====== ADD / EDIT TRIP MODAL ====== */}
      {showAddTripModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddTripModal(false)}>
          <div className="bg-[#0a0a0a] border border-white/30 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">{editingTripDetail ? 'Edit Trip' : 'Add New Trip'}</h2>
                <button onClick={() => setShowAddTripModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors"><X size={18} /></button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {[
                { label: 'Trip Name *', key: 'name', placeholder: 'e.g. Maldives Getaway', type: 'text' },
                { label: 'Destination *', key: 'destination', placeholder: 'e.g. Malé, Maldives', type: 'text' },
                { label: 'Traveling From', key: 'origin', placeholder: 'e.g. New York, USA', type: 'text' },
                { label: 'Hotel / Accommodation', key: 'hotel', placeholder: 'e.g. 5 Star Resort', type: 'text' },
              ].map(field => (
                <div key={field.key}>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">{field.label}</label>
                  <input
                    data-testid={`trip-form-${field.key}`}
                    type={field.type}
                    value={tripFormData[field.key]}
                    onChange={e => setTripFormData({ ...tripFormData, [field.key]: e.target.value })}
                    placeholder={field.placeholder}
                    className="w-full bg-black border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-white/60 transition-colors text-sm"
                  />
                </div>
              ))}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">Start Date</label>
                  <input data-testid="trip-form-start-date" type="date" value={tripFormData.startDate} onChange={e => setTripFormData({ ...tripFormData, startDate: e.target.value })} className="w-full bg-black border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-white/60 transition-colors text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">End Date</label>
                  <input data-testid="trip-form-end-date" type="date" value={tripFormData.endDate} onChange={e => setTripFormData({ ...tripFormData, endDate: e.target.value })} className="w-full bg-black border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-white/60 transition-colors text-sm" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">Category</label>
                  <select data-testid="trip-form-category" value={tripFormData.category} onChange={e => setTripFormData({ ...tripFormData, category: e.target.value })} className="w-full bg-black border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-white/60 transition-colors text-sm">
                    {TRIP_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">Status</label>
                  <select data-testid="trip-form-status" value={tripFormData.status} onChange={e => setTripFormData({ ...tripFormData, status: e.target.value })} className="w-full bg-black border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-white/60 transition-colors text-sm">
                    <option value="upcoming">Upcoming</option>
                    <option value="completed">Completed</option>
                    <option value="planned">Planned</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">Description</label>
                <textarea data-testid="trip-form-description" value={tripFormData.description} onChange={e => setTripFormData({ ...tripFormData, description: e.target.value })} placeholder="Describe your trip..." rows={3} className="w-full bg-black border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-white/60 transition-colors text-sm resize-none" />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">Cover Image URL</label>
                <input data-testid="trip-form-image" type="url" value={tripFormData.image} onChange={e => setTripFormData({ ...tripFormData, image: e.target.value })} placeholder="https://..." className="w-full bg-black border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-white/60 transition-colors text-sm" />
              </div>
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                data-testid="save-trip-btn"
                onClick={handleSaveMyTrip}
                className="flex-1 py-2.5 bg-white text-black font-semibold rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                <Check size={16} /> {editingTripDetail ? 'Save Changes' : 'Add Trip'}
              </button>
              <button
                data-testid="cancel-trip-btn"
                onClick={() => setShowAddTripModal(false)}
                className="px-5 py-2.5 border border-white/30 text-white font-semibold rounded-xl hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== DIALOGS (shared) ==================== */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-black border-white/60 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Trip</DialogTitle>
            <DialogDescription className="text-gray-400">
              Add travel information to your itinerary
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Destination *</label>
                <input
                  type="text"
                  required
                  value={formData.destination}
                  onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="e.g., New York, NY"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">End Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Purpose *</label>
                <input
                  type="text"
                  required
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="Purpose of travel"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                >
                  <option value="planned">Planned</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Flight Details</label>
                <input
                  type="text"
                  value={formData.flight_details}
                  onChange={(e) => setFormData({ ...formData, flight_details: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="e.g., AA 1234"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Hotel Details</label>
                <input
                  type="text"
                  value={formData.hotel_details}
                  onChange={(e) => setFormData({ ...formData, hotel_details: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="Hotel name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="Additional notes"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="border-white/60 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-red-500 hover:bg-red-600 text-white">
                Add Trip
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Flight Dialog */}
      <Dialog
        open={isFlightDialogOpen}
        onOpenChange={(open) => {
          setIsFlightDialogOpen(open);
          if (!open) {
            // Closing the dialog should also reset edit mode
            setEditingFlightId(null);
          }
        }}
      >
        <DialogContent className="bg-black border-white/60 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">{editingFlightId ? 'Edit Flight' : 'Add Flight'}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {editingFlightId ? 'Update the details for this flight' : 'Add a new flight to your travel tracker'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddFlight} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Airline *</label>
                <input
                  type="text"
                  value={flightFormData.airline}
                  onChange={(e) => setFlightFormData({ ...flightFormData, airline: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="e.g., American Airlines"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Flight Number</label>
                <input
                  type="text"
                  value={flightFormData.flight_number}
                  onChange={(e) => setFlightFormData({ ...flightFormData, flight_number: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="e.g., AA1234"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">From (Airport Code) *</label>
                <input
                  type="text"
                  value={flightFormData.departure}
                  onChange={(e) => setFlightFormData({ ...flightFormData, departure: e.target.value.toUpperCase() })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="e.g., LAX"
                  maxLength={4}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">To (Airport Code) *</label>
                <input
                  type="text"
                  value={flightFormData.arrival}
                  onChange={(e) => setFlightFormData({ ...flightFormData, arrival: e.target.value.toUpperCase() })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="e.g., JFK"
                  maxLength={4}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Date *</label>
                <input
                  type="date"
                  value={flightFormData.date}
                  onChange={(e) => setFlightFormData({ ...flightFormData, date: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Time</label>
                <input
                  type="time"
                  value={flightFormData.time}
                  onChange={(e) => setFlightFormData({ ...flightFormData, time: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Notes</label>
              <textarea
                value={flightFormData.notes}
                onChange={(e) => setFlightFormData({ ...flightFormData, notes: e.target.value })}
                className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                placeholder="Additional notes"
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setIsFlightDialogOpen(false); setEditingFlightId(null); }}
                className="border-white/60 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-500 hover:bg-blue-600 text-white">
                {editingFlightId ? 'Save Changes' : 'Add Flight'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Trip Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-black border-white/60 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Trip</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEdit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-300 mb-2 block">Destination *</label>
                <input
                  type="text"
                  required
                  value={formData.destination}
                  onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Start Date *</label>
                <input
                  type="date"
                  required
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">End Date *</label>
                <input
                  type="date"
                  required
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Purpose *</label>
                <input
                  type="text"
                  required
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                >
                  <option value="planned">Planned</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-300 mb-2 block">Flight Details</label>
                <textarea
                  value={formData.flight_details}
                  onChange={(e) => setFormData({ ...formData, flight_details: e.target.value })}
                  rows={2}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-300 mb-2 block">Hotel Details</label>
                <textarea
                  value={formData.hotel_details}
                  onChange={(e) => setFormData({ ...formData, hotel_details: e.target.value })}
                  rows={2}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-300 mb-2 block">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} className="border-white/60 text-white hover:bg-white/5">
                Cancel
              </Button>
              <Button type="submit" className="bg-white hover:bg-cyan-600 text-white">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="bg-black border-white/60 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plane size={20} className="text-cyan-500" />
              Trip Details
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Complete travel information and flight details
            </DialogDescription>
          </DialogHeader>
          {selectedTrip && (
            <div className="space-y-6 py-4">
              {/* Trip Header */}
              <div className="bg-gradient-to-r from-gray-500/10 to-purple-500/10 border border-cyan-500/30 rounded-lg p-4">
                <h3 className="text-2xl font-bold text-white mb-2">{selectedTrip.destination}</h3>
                <p className="text-gray-300 text-sm mb-3">{selectedTrip.purpose}</p>
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    selectedTrip.status === 'upcoming' || selectedTrip.status === 'planned' ? 'bg-white/20 text-cyan-500' :
                    selectedTrip.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                    'bg-black text-gray-300'
                  }`}>
                    {selectedTrip.status}
                  </span>
                  {selectedTrip.category && (
                    <span className="text-xs text-gray-400">
                      Category: <span className="text-white font-semibold">{selectedTrip.category}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Travel Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="backdrop-blur-sm bg-transparent border border-white/60 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar size={16} className="text-cyan-500" />
                    <p className="text-xs font-semibold text-gray-400 uppercase">Departure Date</p>
                  </div>
                  <p className="text-lg font-bold text-white">{selectedTrip.start_date}</p>
                </div>
                <div className="backdrop-blur-sm bg-transparent border border-white/60 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar size={16} className="text-purple-500" />
                    <p className="text-xs font-semibold text-gray-400 uppercase">Return Date</p>
                  </div>
                  <p className="text-lg font-bold text-white">{selectedTrip.end_date}</p>
                </div>
              </div>

              {/* Flight Details */}
              {selectedTrip.flight_details && (
                <div className="bg-transparent border border-cyan-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Plane size={16} className="text-cyan-500" />
                    <h4 className="text-sm font-bold text-white uppercase">Flight Details</h4>
                  </div>
                  <div className="bg-black rounded-lg p-3 border border-white/60">
                    <p className="text-white whitespace-pre-wrap">{selectedTrip.flight_details}</p>
                  </div>
                </div>
              )}

              {/* Hotel Details */}
              {selectedTrip.hotel_details && (
                <div className="bg-transparent border border-purple-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin size={16} className="text-purple-500" />
                    <h4 className="text-sm font-bold text-white uppercase">Hotel Details</h4>
                  </div>
                  <div className="bg-black rounded-lg p-3 border border-white/60">
                    <p className="text-white whitespace-pre-wrap">{selectedTrip.hotel_details}</p>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedTrip.notes && (
                <div className="backdrop-blur-sm bg-transparent border border-white/60 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-gray-400 uppercase mb-3">Additional Notes</h4>
                  <div className="bg-black rounded-lg p-3 border border-white/60">
                    <p className="text-gray-300 whitespace-pre-wrap">{selectedTrip.notes}</p>
                  </div>
                </div>
              )}

              {/* Empty State Messages */}
              {!selectedTrip.flight_details && !selectedTrip.hotel_details && !selectedTrip.notes && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No additional details available for this trip.</p>
                  <p className="text-gray-600 text-sm mt-2">Click Edit to add flight and hotel information.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDetailsDialogOpen(false)}
              className="border-white/60 text-white hover:bg-white/5"
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={() => {
                setIsDetailsDialogOpen(false);
                handleEditTrip(selectedTrip);
              }}
              className="bg-white hover:bg-cyan-600 text-white"
            >
              <Edit2 size={16} className="mr-2" />
              Edit Trip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Boarding Pass Modal — shows real uploaded boarding pass image
          if present; otherwise displays only the structured flight info
          parsed from the source (no fabricated seat/gate/QR). */}
      <Dialog open={isBoardingPassOpen} onOpenChange={setIsBoardingPassOpen}>
        <DialogContent className="bg-white text-black max-w-md p-0 overflow-hidden">
          {selectedFlight && (
            <div className="boarding-pass">
              {/* Header */}
              <div className={`p-4 text-white ${selectedFlight.type === 'return' ? 'bg-orange-500' : 'bg-blue-600'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <span className="text-black font-bold text-lg">{selectedFlight.airlineCode}</span>
                    </div>
                    <div>
                      <p className="font-bold text-lg">{selectedFlight.airline}</p>
                      <p className="text-white/80 text-sm">{selectedFlight.flightNumber || '—'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-white/70">BOARDING PASS</p>
                    <p className="font-bold">{selectedFlight.type === 'return' ? 'RETURN' : 'OUTBOUND'}</p>
                  </div>
                </div>
              </div>

              {/* Real uploaded boarding pass image (if available) */}
              {selectedFlight.boardingPassFileId && (
                <div className="p-4 bg-black flex justify-center">
                  <img
                    src={`${API_URL}/api/flights/manual/${encodeURIComponent(selectedFlight.id)}/boarding-pass?t=${selectedFlight.boardingPassUploadedAt || ''}`}
                    alt="Boarding Pass"
                    className="max-w-full max-h-[400px] object-contain rounded"
                    data-testid="real-boarding-pass-image"
                  />
                </div>
              )}

              {/* Flight Route */}
              <div className="p-6 bg-gray-50">
                <div className="flex items-center justify-between mb-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-gray-900 leading-none">{selectedFlight.departure || '—'}</p>
                    {getAirportCity(selectedFlight.departure) && (
                      <p className="text-xs text-gray-700 mt-1 font-medium">{getAirportCity(selectedFlight.departure)}</p>
                    )}
                    <p className="text-sm text-gray-500 mt-1">Departure</p>
                  </div>
                  <div className="flex-1 flex items-center justify-center px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <div className="h-0.5 w-16 bg-gradient-to-r from-blue-500 to-orange-500"></div>
                      <Plane size={24} className="text-gray-700 transform rotate-90" />
                      <div className="h-0.5 w-16 bg-gradient-to-r from-orange-500 to-blue-500"></div>
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-gray-900 leading-none">{selectedFlight.arrival || '—'}</p>
                    {getAirportCity(selectedFlight.arrival) && (
                      <p className="text-xs text-gray-700 mt-1 font-medium">{getAirportCity(selectedFlight.arrival)}</p>
                    )}
                    <p className="text-sm text-gray-500 mt-1">Arrival</p>
                  </div>
                </div>

                {/* Real Flight Details — only shows fields that actually exist */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase">Date</p>
                    <p className="font-bold text-gray-900 text-sm">{selectedFlight.dateFormatted || '—'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase">Time</p>
                    <p className="font-bold text-gray-900">{selectedFlight.time ? formatFlightTime12h(selectedFlight.time) : 'TBD'}</p>
                  </div>
                  {selectedFlight.gate && (
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase">Gate</p>
                      <p className="font-bold text-gray-900">{selectedFlight.gate}</p>
                    </div>
                  )}
                  {selectedFlight.terminal && (
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase">Terminal</p>
                      <p className="font-bold text-gray-900">{selectedFlight.terminal}</p>
                    </div>
                  )}
                  {selectedFlight.seat && (
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase">Seat</p>
                      <p className="font-bold text-gray-900 text-xl">{selectedFlight.seat}</p>
                    </div>
                  )}
                  {selectedFlight.passengerName && (
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase">Passenger</p>
                      <p className="font-bold text-gray-900 text-sm">{selectedFlight.passengerName}</p>
                    </div>
                  )}
                  {(selectedFlight.confirmationCode || selectedFlight.pnr) && (
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-200 col-span-2">
                      <p className="text-xs text-gray-500 uppercase">Confirmation</p>
                      <p className="font-mono font-bold text-lg text-gray-900">{selectedFlight.confirmationCode || selectedFlight.pnr}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer with upload / remove controls */}
              <div className="p-4 bg-white border-t border-gray-200 space-y-2">
                {selectedFlight.source === 'manual' ? (
                  <>
                    <label
                      className="block w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium text-center cursor-pointer transition-colors"
                      data-testid="upload-boarding-pass-btn"
                    >
                      {selectedFlight.boardingPassFileId ? 'Replace Boarding Pass' : 'Upload Real Boarding Pass'}
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleUploadBoardingPass(selectedFlight, file).then(() => {
                              // Pull the updated card back into selectedFlight so the
                              // image renders without closing the modal
                              setTimeout(() => {
                                setFlightCards(prev => {
                                  const updated = prev.find(c => c.id === selectedFlight.id);
                                  if (updated) setSelectedFlight(updated);
                                  return prev;
                                });
                              }, 200);
                            });
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {selectedFlight.boardingPassFileId && (
                      <button
                        onClick={(e) => {
                          handleRemoveBoardingPass(selectedFlight, e).then(() => {
                            setSelectedFlight(prev => prev ? { ...prev, boardingPassFileId: null, boardingPassUploadedAt: null } : prev);
                          });
                        }}
                        className="block w-full px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 text-xs rounded-lg font-medium border border-red-200"
                        data-testid="remove-boarding-pass-btn"
                      >
                        Remove Boarding Pass
                      </button>
                    )}
                    <p className="text-[11px] text-gray-500 text-center">
                      Upload an image or PDF — the barcode will be decoded automatically.
                    </p>
                  </>
                ) : (
                  <p className="text-[11px] text-gray-500 text-center">
                    This flight was parsed from your calendar. To attach a real boarding pass, add it as a manual flight.
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Travel Category Management Modal */}
      {showTravelCategoryModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowTravelCategoryModal(false)}>
          <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Edit Travel Categories</h3>
              <button onClick={() => setShowTravelCategoryModal(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="p-4 border-b border-gray-700">
              <div className="flex gap-2 mb-3">
                <div className="relative">
                  <button className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center" onClick={(e) => e.currentTarget.nextElementSibling.classList.toggle('hidden')}>{newTravelCategoryIcon}</button>
                  <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-5 gap-1 w-40">
                    {travelCategoryIcons.map(icon => (<button key={icon} onClick={(e) => { setNewTravelCategoryIcon(icon); e.currentTarget.parentElement.classList.add('hidden'); }} className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center">{icon}</button>))}
                  </div>
                </div>
                <input type="text" value={newTravelCategoryName} onChange={(e) => setNewTravelCategoryName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTravelCategory()} placeholder="New category name..." className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" data-testid="new-travel-category-input" />
              </div>
              <div className="flex gap-2 items-center mb-3">
                <span className="text-xs text-gray-400">Color:</span>
                <div className="flex gap-1 flex-wrap">
                  {travelCategoryColors.map(color => (<button key={color} onClick={() => setNewTravelCategoryColor(color)} className={`w-6 h-6 rounded-full ${color} ${newTravelCategoryColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : ''}`} />))}
                </div>
              </div>
              <button onClick={addTravelCategory} disabled={!newTravelCategoryName.trim()} className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5" data-testid="add-travel-category-btn"><Plus size={14} />Add Category</button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto space-y-2">
              {customTravelCategories.filter(c => c.id !== 'all').map(cat => (
                <div key={cat.id} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group">
                  {editingTravelCategory === cat.id ? (
                    <>
                      <span className="text-lg">{cat.icon}</span>
                      <input type="text" value={editTravelCategoryValue} onChange={(e) => setEditTravelCategoryValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') updateTravelCategory(cat.id, editTravelCategoryValue); if (e.key === 'Escape') { setEditingTravelCategory(null); setEditTravelCategoryValue(''); }}} className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm" autoFocus />
                      <button onClick={() => updateTravelCategory(cat.id, editTravelCategoryValue)} className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"><Check size={14} /></button>
                      <button onClick={() => { setEditingTravelCategory(null); setEditTravelCategoryValue(''); }} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <span className={`w-8 h-8 rounded-lg ${cat.color} flex items-center justify-center text-sm`}>{cat.icon}</span>
                      <span className="flex-1 text-white text-sm">{cat.name}{['business', 'personal'].includes(cat.id) && <span className="ml-2 text-xs text-gray-500">(default)</span>}</span>
                      <button onClick={() => { setEditingTravelCategory(cat.id); setEditTravelCategoryValue(cat.name); }} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100"><Pencil size={14} /></button>
                      {!['business', 'personal'].includes(cat.id) && <button onClick={() => deleteTravelCategory(cat.id)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-700">
              <button onClick={() => setShowTravelCategoryModal(false)} className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium" data-testid="done-travel-category-btn">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Travel;